import type { ILang } from '@core/interfaces/ILang';

const lang: ILang = {
  alert: {
    abort: 'Annuleren',
    buy: 'Kopen',
    cancel: 'Annuleren',
    caption: 'Fout',
    close: 'Sluiten',
    confirm: 'Bevestigen',
    dont_save: 'Niet opslaan',
    dont_show_again: 'Niet meer weergeven',
    duplicated_preset_name: 'Dubbele voorinstellingsnaam',
    error: 'FOUT',
    info: 'INFO',
    instruction: 'Instructie',
    learn_more: 'Meer informatie',
    no: 'Nee',
    ok: 'OK',
    ok2: 'OK',
    oops: 'Oeps...',
    retry: 'Opnieuw proberen',
    save: 'Opslaan',
    stop: 'Stoppen',
    warning: 'WAARSCHUWING',
    yes: 'Ja',
  },
  auto_fit: {
    artwork_size: 'Grootte van het kunstwerk',
    error_tip1: '1. Is het kunstwerk correct op het materiaal geplaatst?',
    error_tip2: '2. Zijn de materiaalcontouren duidelijk genoeg voor herkenning?',
    failed_to_find_contour: 'Geen contouren gevonden voor automatische aanpassing',
    how_to_improve_accuracy: 'Hoe kan de detectienauwkeurigheid worden verbeterd?',
    how_to_improve_accuracy_url: 'https://support.flux3dp.com/hc/en-us/articles/15792349372687',
    learn_more: 'Bekijk de tutorial',
    learn_more_url: 'https://support.flux3dp.com/hc/en-us/articles/10273384373775',
    offset_x: 'Offset X',
    offset_y: 'Offset Y',
    pattern_size: 'Patroongrootte',
    position_artwork: 'Kunstwerk positioneren',
    position_step1: 'Pas de grootte en positie van uw kunstwerk op het patroon aan.',
    position_step2: 'Klik op "Toepassen" om Auto Fit op dit patroon toe te passen.',
    preview_first: 'Voer eerst een cameravoorvertoning uit.',
    reset_position: 'Positie resetten',
    retake: 'Foto opnieuw nemen',
    retake_tooltip: 'Camerafoto opnieuw nemen op de positie van elk object voor betere detectie',
    retry_with_remove_bg: 'Achtergrond verwijderen',
    retry_with_remove_bg_tooltip: 'Achtergrond verwijderen uit voorbeeldafbeelding om contourdetectie te verbeteren',
    rotation: 'Draaien',
    select_a_pattern: 'Kies een patroon',
    selected_artwork: 'Geselecteerd kunstwerk',
    step1: 'Kies een patroon waar u het kunstwerk wilt plaatsen.',
    step2: 'Als er geen patroon wordt gevonden, bekijk de cameravoorkeur opnieuw en pas Auto Aanpassen toe.',
    step3: 'Als het nog steeds niet lukt om het juiste patroon te vinden, zorg er dan voor dat uw werkstuk duidelijk herkenbaar is en dat de werkruimte vrij is van vuil.',
    title: 'Auto Aanpassen',
    use_original_image: 'Gebruik originele afbeelding',
    use_original_image_tooltip: 'Herstel de originele voorbeeldafbeelding zonder achtergrondverwijdering',
    warning_bg_removal: 'Deze bewerking voert de functie "Achtergrond verwijderen" uit, die 0,02 Credit verbruikt. Wilt u doorgaan?',
    warning_retake_preview: 'Deze bewerking is een gedeeltelijke herneming. Nieuwe afbeeldingen en achtergrond worden toegevoegd. Wilt u doorgaan?',
  },
  autosave: {
    open_settings: 'Instellingen openen',
    path_not_correct: 'De locatie voor automatisch opslaan bestaat niet meer.',
    path_not_correct_desc: 'Kies een nieuwe map in <b>%(preferences)s > %(autosave)s > %(path)s</b> om uw werk te blijven opslaan.',
  },
  barcode_generator: {
    bar_height: 'Balkhoogte',
    bar_width: 'Balkbreedte',
    barcode: {
      invalid_value: 'De waarde is ongeldig voor het geselecteerde formaat.',
    },
    font: 'Lettertype',
    font_size: 'Lettergrootte',
    hide_text: 'Tekst verbergen',
    invert_color: 'Kleur omkeren',
    text: 'Tekst',
    text_margin: 'Tekstmarge',
  },
  beambox: {
    ai_generate: {
      dimensions: {
        more: 'Meer',
        ratio: 'Verhouding',
        resolution: 'Resolutie',
        title: 'Afbeeldingsafmetingen',
      },
      error: {
        config_description: 'Kan AI-configuratie niet laden',
        config_message: 'Kan AI-stijlen niet laden',
        generation_failed: 'Generatie mislukt',
        import_failed: 'Import mislukt',
        insufficient_credits: 'Onvoldoende credits',
      },
      form: {
        count: 'Aantal',
        credit_required: 'Benodigde credits',
        editor: 'Bewerken',
        generate: 'Genereren',
        laser_friendly: 'Laservriendelijk',
      },
      header: {
        history_tooltip: 'Geschiedenis',
        reset_tooltip: 'Alles resetten',
        title: 'AI-creatie',
      },
      history: {
        detail: 'Details',
        empty_description: 'Nog geen generatiegeschiedenis. Begin met creëren!',
        error_message: 'Kan geschiedenis niet laden',
        not_generated: '❌ Niet gegenereerd',
        recreate: 'Opnieuw maken',
        status_failed: 'Mislukt',
        status_generating: 'Bezig met genereren',
        status_success: 'Geslaagd',
        storage_notice: 'Er worden maximaal 10 recente items opgeslagen. Gegenereerde afbeeldingen worden 3 dagen bewaard.',
        title: 'Generatiegeschiedenis',
      },
      loading: {
        history: 'Geschiedenis laden...',
        styles: 'AI-stijlen laden...',
        tip_0: 'Creatieve motor wordt gestart…',
        tip_1: 'Pixels worden gepolijst… ✨',
        tip_2: 'Bijna klaar, details worden verfijnd',
        tip_3: 'Nog aan het werken, dit deel heeft wat meer tijd nodig',
        tip_4: 'Grote ideeën kosten tijd, bedankt voor uw geduld',
      },
      results: {
        download: 'Downloaden',
        import: 'Importeren',
        title: 'Resultaten',
      },
      retry: 'Opnieuw proberen',
      style: {
        apply: 'Stijl toepassen',
        choose: 'Stijl kiezen',
        custom_creation: 'Aangepaste creatie',
        customize: 'Aanpassen',
        select: 'Selecteer creatiestijl',
      },
      upload: {
        add: 'Toevoegen',
        click_or_drag: 'Klik of sleep afbeeldingen hierheen',
        error: 'Uploadfout',
        file_size_error: '%s: Bestandsgrootte moet kleiner zijn dan %sMB',
        file_type_error: '%s: Alleen JPEG-, PNG- en WebP-afbeeldingen worden ondersteund',
        from_history: 'Uit geschiedenis',
        images: 'Afbeeldingen uploaden',
        max_images_error: 'Maximaal %s afbeeldingen toegestaan',
        selected: '%s / %s geselecteerd',
        supported_formats: 'JPEG, PNG, WebP • Max %s',
      },
      validation: {
        description_or_image_required: 'Geef een beschrijving of upload minimaal één afbeelding',
        field_exceeds_max_length: '"%s" overschrijdt de maximale lengte van %s tekens.',
        field_required: '"%s" is verplicht. Vul dit veld in.',
        login_required: 'Log in om AI-generatie te gebruiken.',
        max_images: 'Maximaal %s afbeeldingen toegestaan',
      },
    },
    announcement_panel: {
      title: 'Aankondiging',
    },
    banner: {
      auto_feeder: 'Automatische Voedermodus',
      autofocus1: 'Autofocus Modus',
      autofocus2: 'Klik ergens om autofocus uit te voeren.',
      camera_preview_borderless_mode: '(Open Onderkant)',
      curve_engraving: 'Kromme Graveermodus',
      pass_through: 'Doorvoer Modus',
      rotary: 'Rotatiemodus',
    },
    bottom_right_panel: {
      convert_text_to_path_before_export: 'Tekst naar pad omzetten...',
      export_file_error_ask_for_upload: 'Export mislukt. Wilt u de werkende scène naar het ontwikkelteam sturen voor een bugrapport?',
      retreive_image_data: 'Gegevens ophalen...',
    },
    context_menu: {
      copy: 'Kopiëren',
      cut: 'Knippen',
      delete: 'Verwijderen',
      duplicate: 'Dupliceren',
      group: 'Groeperen',
      move_back: 'Naar achtergrond verplaatsen',
      move_down: 'Naar achteren verplaatsen',
      move_front: 'Naar voorgrond verplaatsen',
      move_up: 'Naar voren verplaatsen',
      paste: 'Plakken',
      paste_in_place: 'Plakken op plaats',
      ungroup: 'Degroeperen',
    },
    document_panel: {
      add_on: 'Add-ons',
      auto_feeder: 'Automatische voeder',
      auto_feeder_url: 'https://support.flux3dp.com/hc/en-us/articles/11688230498575',
      auto_shrink: 'Automatisch verkleinen',
      auto_shrink_tooltip: 'Geldt alleen voor lagen met een resolutie van 250 DPI of hoger.',
      auto_shrink_url: 'https://support.flux3dp.com/hc/en-us/articles/12997377035919',
      borderless_mode: 'Open onderzijde',
      current_position: 'Huidige Positie',
      disable: 'Uitschakelen',
      document_settings: 'Documentinstellingen',
      door_protect: 'Deurbescherming',
      door_protect_desc: 'Alleen beschikbaar met Promark Safe+.',
      enable_autofocus: 'Autofocus',
      enable_diode: 'Diode laser',
      enable_nozzle_refresh_area: 'Nozzle verversingsgebied inschakelen',
      extend_y_area: 'Y-gebied uitbreiden',
      frame_before_start: 'Frame voordat uitgevoerd wordt',
      frame_before_start_url: 'https://support.flux3dp.com/hc/en-us/articles/11494925637135',
      job_origin: 'Werk Oorsprong',
      laser_source: 'Laserbron',
      machine: 'Machine',
      manual: 'Handmatig',
      notification: {
        changeFromPrintingWorkareaTitle: 'Wilt u de printlagen converteren naar laslagen?',
      },
      origin: 'Oorsprong',
      pass_through: 'Doorgang',
      pass_through_height_desc: 'Voer de lengte van het object in om het werkgebied uit te breiden.',
      prespray_times: 'Aantal reinigingen',
      rotary_mode: 'Roterend',
      scale: 'Schaal',
      skip_prespray: 'Reiniging overslaan',
      start_from: 'Start Vanaf',
      start_position: 'Startpositie',
      start_work_button: 'Uitvoeren Knop',
      workarea: 'Werkgebied',
    },
    elements_panel: {
      animals: 'Dier',
      arrow: 'Pijl',
      avatar: 'Avatar',
      basic: 'Basis',
      birds: 'Vogel',
      browse_categories: 'Blader door categorieën',
      celebration: 'Viering',
      circular: 'Cirkelvormig',
      cleaning_tools: 'Schoonmaakgereedschap',
      CNY: 'Chinees nieuwjaar',
      corner: 'Hoek',
      decor: 'Decoratie',
      doctor: 'Dokter',
      easter: 'Pasen',
      elements: 'Elementen',
      environment: 'Omgeving',
      gardening_tools: 'Tuingereedschap',
      graphics: 'Grafisch',
      halloween: 'Halloween',
      hammer_wrench: 'Hamer en moersleutel',
      heart: 'Hart',
      holidays: 'Feestdag',
      insect: 'Insect',
      kitchen_tools: 'Keukengerei',
      label: 'Label',
      land: 'Land',
      line: 'Lijn',
      load_more: 'Laad de volgende 100 resultaten',
      nature: 'Natuur',
      people: 'Mensen',
      photo: 'Foto',
      plants: 'Planten',
      police: 'Politie',
      recently_used: 'Recent gebruikt',
      ribbon: 'Lint',
      sea: 'Zee',
      search_examples: 'Voorbeeld',
      search_invalid_characters: 'Ongeldige tekens gebruikt.',
      search_no_results: 'Er zijn geen resultaten gevonden voor "%s". Controleer de spelling of probeer een ander trefwoord.',
      search_with_english: "Alleen Engelse letters, cijfers, komma's en spaties zijn toegestaan.",
      shape: 'Vorm',
      sign_in: 'Log in om meer dan 10.000 gratis iconen te krijgen',
      speech: 'Spraak',
      student: 'Student',
      study_tools: 'Studiehulpmiddelen',
      teacher: 'Leraar',
      text: 'Tekstframe',
      title: 'Elementen',
      tools: 'Gereedschappen',
      valentines: 'Valentijnsdag',
      weather: 'Weer',
      Xmas: 'Kerstmis',
      zodiac: 'Sterrenbeeld',
    },
    image_trace_panel: {
      back: 'Terug',
      brightness: 'Helderheid',
      cancel: 'Annuleren',
      contrast: 'Contrast',
      next: 'Volgende',
      okay: 'OK',
      threshold: 'Drempel',
      tuning: 'Parameters',
    },
    layer_color_config_panel: {
      add: 'Toevoegen',
      add_config: 'Kleur toevoegen',
      color: 'Kleur',
      default: 'Terugzetten naar standaard',
      in_use: 'Deze kleur wordt gebruikt.',
      layer_color_config: 'Laagkleurconfiguraties',
      no_input: 'Voer een geldige hex-kleurcode in.',
      power: 'Vermogen',
      repeat: 'Uitvoeren',
      speed: 'Snelheid',
      sure_to_delete: 'Weet u zeker dat u deze kleurinstelling wilt verwijderen?',
      sure_to_reset: 'U verliest alle aangepaste parameters, weet u zeker dat u terug wilt naar standaard?',
    },
    left_panel: {
      borderless_blind_area: 'Niet-graverend gebied',
      borderless_preview: 'Open onderkant modus camerabeeld',
      diode_blind_area: 'Blind gebied hybride laseradd-on',
      label: {
        adjust_height: 'Hoogte aanpassen',
        array: 'Array',
        boxgen: 'Boxgen',
        choose_camera: 'Camera',
        clear_preview: 'Voorbeeld wissen',
        cursor: 'Selecteer',
        curve_engraving: {
          clear_area: 'Geselecteerd gebied wissen',
          exit: 'Afsluiten',
          preview_3d_curve: 'Voorbeeld van kromme graveren',
          select_area: 'Gebied selecteren',
          title: 'Kromme graveren',
        },
        elements: 'Vormen',
        end_preview: 'Liveweergave stoppen',
        fit_text: 'Tekstvak',
        line: 'Lijn',
        live_feed: 'Live beeld',
        my_cloud: 'Mijn cloud',
        oval: 'Ovaal',
        pass_through: 'Doorgang',
        pen: 'Pen',
        photo: 'Afbeelding',
        polygon: 'Veelhoek',
        preview: 'Liveweergave',
        preview_precise: 'Nauwkeurig cameravoorbeeld',
        preview_wide_angle: 'Voorbeeld van groothoekcamera',
        qr_code: 'QR-code',
        rect: 'Rechthoek',
        text: 'Tekst',
        trace: 'Afbeelding traceren',
      },
      unpreviewable_area: 'Blind gebied',
    },
    network_testing_panel: {
      average_response: 'Gemiddelde reactietijd',
      cannot_connect_1: '#840 Kan geen verbinding maken met doel-IP.',
      cannot_connect_2: '#840 Kan geen verbinding maken met doel-IP. Zorg ervoor dat het doel zich in hetzelfde netwerk bevindt.',
      cannot_get_local: 'Ophalen lokaal IP-adres mislukt',
      connection_quality: 'Verbindingskwaliteit',
      device_not_on_list: '#842 Machine staat niet in lijst, maar verbinding boven 70% en reactietijd onder 100ms',
      empty_ip: '#818 Voer eerst het doelapparaat IP-adres in.',
      end: 'Einde',
      fail_to_start_network_test: '#817 Kan netwerktest niet starten',
      hint_connect_camera_timeout: 'Time-out bij starten camera voorbeeld?',
      hint_connect_failed_when_sending_job: 'Mislukt om verbinding te maken bij versturen van taak?',
      hint_device_often_on_list: 'Staat de machine vaak niet in de lijst?',
      insert_ip: 'Doelapparaat IP-adres:',
      invalid_ip: '#818 Ongeldig IP-adres',
      ip_startswith_169: '#843 Machine IP-adres begint met 169.254',
      link_connect_camera_timeout: 'https://support.flux3dp.com/hc/en-us/articles/360001791895',
      link_connect_failed_when_sending_job: 'https://support.flux3dp.com/hc/en-us/articles/360001841656',
      link_device_often_on_list: 'https://support.flux3dp.com/hc/en-us/articles/360001841636',
      linux_permission_hint: 'Deze fout komt meestal door onvoldoende rechten.\nVoer alsjeblieft "sudo beam-studio --no-sandbox" uit in terminal om rechten te krijgen en de netwerktest uit te voeren.',
      local_ip: 'Lokaal IP-adres:',
      network_testing: 'Netwerktest',
      network_unhealthy: '#841 Verbinding onder 70% of reactietijd boven 100ms',
      start: 'Start',
      test_completed: 'Test voltooid',
      test_fail: 'Test mislukt',
      testing: 'Netwerk testen...',
    },
    object_panels: {
      lock_desc: 'Breedte-hoogteverhouding behouden (SHIFT)',
      text_to_path: {
        check_thumbnail_warning: 'Sommige teksten zijn veranderd in andere lettertypen bij het converteren van tekst naar paden en sommige tekens zijn mogelijk niet correct geconverteerd.\nControleer de voorbeeldafbeelding opnieuw voordat u de taak verstuurt.',
        error_when_parsing_text: 'Fout bij het converteren van tekst naar pad',
        font_substitute_pop: 'Uw tekst bevat tekens die niet worden ondersteund door het huidige lettertype. <br/>Wilt u <strong>%s</strong> gebruiken als vervanging?',
        retry: 'Probeer het later opnieuw of kies een ander lettertype',
        use_current_font: 'Huidig lettertype gebruiken',
      },
      wait_for_parsing_font: 'Lettertypen aan het verwerken... Een moment geduld',
    },
    path_preview: {
      current_position: 'Huidige positie',
      cut_distance: 'Snijafstand',
      cut_time: 'Snijtijd',
      end_preview: 'Voorbeeld beëindigen',
      estimated_time: 'Geschatte totale tijd',
      invert: 'Omkeren',
      pause: 'Pauzeren',
      play: 'Afspelen',
      play_speed: 'Afspeelsnelheid',
      preview_info: 'Voorbeeldinformatie',
      rapid_distance: 'Reisafstand',
      rapid_time: 'Reistijd',
      remark: '* Alle informatie is een geschatte waarde als referentie.',
      size: 'Grootte',
      start_here: 'Start hier',
      stop: 'Stoppen',
      travel_path: 'Reispad',
    },
    photo_edit_panel: {
      aspect_ratio: 'Beeldverhouding',
      brightness: 'Helderheid',
      brightness_and_contrast: 'Helderheid / Contrast',
      circumference: 'Omtrek',
      compare: 'Vergelijken',
      contrast: 'Contrast',
      crop: 'Bijsnijden',
      curve: 'Kromming',
      diameter: 'Diameter',
      free: 'Vrij',
      invert: 'Kleuren omkeren',
      original: 'Origineel',
      phote_edit: 'Foto bewerken',
      processing: 'Verwerken',
      radius: 'Straal',
      rotary_warped: 'Roterend Vervormd',
      rotary_warped_link: 'https://support.flux3dp.com/hc/en-us/articles/10828006201103',
      sharpen: 'Scherpte',
      sharpness: 'Scherpte',
      start: 'Starten',
      warp: 'Vervormen',
    },
    popup: {
      ai_credit: {
        buy_link: 'https://member.flux3dp.com/en-US/credit',
        insufficient_credit: 'Onvoldoende tegoed',
        insufficient_credit_msg: 'Deze functie vereist %s AI-credits, maar je hebt nog maar %s AI-credits over.\nKoop alsjeblieft meer credits en probeer het opnieuw.',
        relogin_to_use: 'Log opnieuw in om deze functie te gebruiken.',
      },
      auto_feeder_origin: 'Gebruik de kaderweergave om het pad en gebied van de laserkop te controleren. Pas vervolgens de materiaalplaatsing of de startpositie van de gravure aan op basis van de resultaten om botsingen tijdens het graveerproces te voorkomen.',
      auto_switch_tab: {
        message: 'Een nieuwe optie om automatisch te schakelen tussen het lagenpaneel en het objectenpaneel is toegevoegd aan de Voorkeuren. Deze optie is standaard uitgeschakeld. Wilt u automatische schakeling nu inschakelen? <br/>U kunt deze instelling op elk moment wijzigen in Voorkeuren.',
        title: 'Automatisch Wisselen Tussen Lagen- en Objectenpaneel',
      },
      backend_connect_failed_ask_to_upload: '#802 Fouten blijven optreden bij verbinden met de backend. Wilt u uw bugrapport uploaden?',
      backend_error_hint: 'Functies werken mogelijk niet goed vanwege een backendfout.',
      both_power_and_speed_too_high: 'Gebruik van lagere lasersterkte verlengt de levensduur van de laserbuis.\nTe hoge snelheid bij deze resolutie kan resulteren in lagere kwaliteit van arcering.',
      bug_report: 'Bugrapportage',
      change_workarea_before_preview: 'Het werkgebied van %s komt niet overeen met het huidige werkgebied. Wilt u overschakelen naar het huidige werkgebied?',
      convert_to_path_fail: 'Converteren naar pad is mislukt.',
      disassemble_use: {
        clip_path_warning: 'Dit bestand bevat een afsnijdmasker en kan niet worden ontleed.',
        execute_time_warning: 'Dit zal geïmporteerde DXF/SVG ontgroeperen. Omdat er veel elementen kunnen zijn, kan het even duren. Weet u zeker dat u wilt doorgaan?',
      },
      dxf_bounding_box_size_over: 'De tekeninggrootte is buiten het werkgebied. Verplaats uw tekening dichter bij de oorsprong in uw CAD-software, of zorg dat de eenheid correct is.',
      dxf_paste_failed: 'Kan DXF niet vanaf het klembord plakken. Zorg ervoor dat de gekopieerde inhoud geldige DXF is.',
      dxf_version_waring: 'De DXF-versie is niet 2013, er kunnen compatibiliteitsproblemen zijn.',
      facebook_group_invitation: {
        already_joined: 'Al lid',
        join_now: 'Ik doe mee',
        later: 'Misschien later',
        message: 'Word lid van onze officiële Facebook-groep om in contact te komen met andere FLUX-lasergebruikers, FLUX-lasers te bespreken, laserkunstwerken te delen en op de hoogte te blijven van de nieuwste updates over onze producten. We kijken ernaar uit u daar te zien!',
        title: 'Word lid van de officiële FLUX gebruikersgroep',
      },
      failed_to_load_svg: 'Kan SVG-gegevens niet laden',
      import_file_contain_invalid_path: '#808 Geïmporteerd SVG-bestand bevat ongeldig pad naar afbeelding. Zorg ervoor dat alle afbeeldingen bestaan of inbed de afbeelding in het bestand',
      import_file_error_ask_for_upload: 'Importeren van SVG-bestand mislukt. Wilt u het bestand beschikbaar stellen aan het ontwikkelteam voor bugrapportage?',
      import_svg: {
        advice_message: 'Dit kan ernstige vertraging of importfouten veroorzaken.',
        advice_message_2: 'Het wordt aanbevolen om het bestand voor import te vereenvoudigen.',
        confirmation_message: 'Wilt u doorgaan?',
        element_count_warning: 'Aantal elementen: %s (Aanbevolen: < %s).',
        file_size_warning: 'Bestandsgrootte: %sMB (Aanbevolen: < %sMB).',
        intro_message: 'Het bestand is mogelijk te groot of te complex:',
        path_d_command_count_warning: 'Aantal opdrachten: %s (Aanbevolen: < %s).',
        title: 'Waarschuwing Bestand Importeren',
      },
      layer_by_color: 'Kleur',
      layer_by_layer: 'Laag',
      loading_image: 'Afbeelding laden, even geduld...',
      more_than_two_object: 'Te veel objecten. Ondersteuning voor slechts 2 objecten',
      mouse: 'Muis',
      no_curve_engraving_with_modules: 'Deze taak bevat graveren op een gebogen oppervlak, wat niet samen met kleurenprinten kan worden gebruikt. Schakel de kleurenprintlaag uit en verzend de taak opnieuw.',
      no_support_text: 'Beam Studio ondersteunt momenteel geen teksttag. Zet tekst om naar pad voordat u importeert.',
      nolayer: 'Enkele laag',
      not_support_object_type: 'Objecttype wordt niet ondersteund',
      or_turn_off_borderless_mode: 'Of schakel de modus zonder onderrand uit.',
      path_engine_upgrade: {
        caption: 'Snellere taakgeneratie beschikbaar',
        keep_current: 'Huidig behouden',
        message: 'We hebben de taakberekeningsengine geüpgraded om de prestaties te verbeteren en het genereren van taken te versnellen.\nWilt u overschakelen naar de nieuwe versnelde engine?\nU kunt dit op elk moment uitschakelen in %(preferences)s > %(editor)s > %(path_acceleration)s.',
      },
      pdf2svg: {
        error_pdf2svg_not_found: '#825 Fout: opdracht pdf2svg niet gevonden. Installeer pdf2svg met uw pakketbeheerder.',
        error_when_converting_pdf: '#824 Fout bij converteren van PDF naar SVG:',
      },
      progress: {
        calculating: 'Berekenen',
        uploading: 'Uploaden',
      },
      questionnaire: {
        caption: 'Help ons met het invullen van de vragenlijst',
        message: 'Help ons met het invullen van de vragenlijst om Beam Studio beter te maken.',
        no_questionnaire_available: 'Er is momenteel geen vragenlijst beschikbaar.',
        unable_to_get_url: 'Het ophalen van de link naar de vragenlijst via internet is mislukt. Controleer uw internetverbinding.',
      },
      reach_tab_count_limit: 'Tablimiet bereikt',
      recommend_downgrade_software: 'We hebben een oudere firmwareversie gedetecteerd. We werken actief aan compatibiliteitsproblemen, maar voor nu raden we aan terug te keren naar <a target="_blank" href="https://beamstudio.s3.ap-northeast-1.amazonaws.com/mac/Beam+Studio+1.9.5.dmg">Beam Studio 1.9.5</a>.',
      recommend_upgrade_firmware: 'We hebben een oudere firmwareversie gedetecteerd. We werken actief aan compatibiliteitsproblemen, maar voor nu raden we aan de firmware bij te werken naar de laatste versie.',
      save_unsave_changed: 'Wilt u de niet-opgeslagen wijzigingen opslaan?',
      select_at_least_two: 'Selecteer twee objecten om door te gaan',
      select_first: 'Selecteer eerst een object.',
      select_import_method: 'Selecteer de laagstijl:',
      select_import_module: 'Selecteer module:',
      sentry: {
        message: 'Gaat u ermee akkoord om relevante informatie automatisch te uploaden naar het ontwikkelingsteam wanneer er fouten optreden?',
        title: 'Laten we Beam Studio samen beter maken',
      },
      should_update_firmware_to_continue: '#814 Uw firmware ondersteunt deze versie van Beam Studio niet. Werk de firmware bij om door te gaan. (Menu > Machine > [Uw machine] > Firmware bijwerken)',
      speed_too_high_lower_the_quality: 'Te hoge snelheid bij deze resolutie kan resulteren in lagere kwaliteit van arcering.',
      still_continue: 'Doorgaan',
      successfully_uploaded: 'Uploaden van bestand gelukt.',
      svg_1_1_waring: 'Deze SVG-versie is 1.1, er kunnen compatibiliteitsproblemen zijn.',
      svg_image_path_waring: 'Dit SVG-bestand bevat <image> paden. Dit kan mislukken bij laden.\nGebruik ingesloten afbeeldingen bij exporteren van SVG om dit te voorkomen.',
      text_to_path: {
        caption: 'Tekst naar pad Converter 2.0',
        message: "Beam Studio introduceert nu een nieuwe tekst naar pad converter (Converter 2.0), die betrouwbaardere resultaten oplevert! Wilt u nu overschakelen? \nU kunt deze instelling later ook aanpassen in het gedeelte 'Tekst naar pad Converter' binnen de voorkeuren.",
      },
      too_fast_for_auto_feeder: 'Het gebruik van te hoge snelheden in lagen die padobjecten bevatten, kan de snijprecisie verminderen.\nWe raden aan om geen snelheden hoger dan %(limit)s te gebruiken bij het snijden met Auto Feeder.',
      too_fast_for_auto_feeder_and_constrain: 'De volgende lagen: %(layers)s \nbevatten vectorpadobjecten en hebben een snelheid die %(limit)s overschrijdt.\nDe snijsnelheid van vectorpadobjecten met Auto Feeder wordt beperkt tot %(limit)s.\nDeze beperking kan worden verwijderd in de Voorkeuren Instellingen.',
      too_fast_for_curve: 'In curve-graveermodus kan de huidige snelheid te hoog zijn en de graveerdetails voor lagen beïnvloeden: %(layers)s.\nOverweeg de snelheid te verlagen voor betere kwaliteit.',
      too_fast_for_path: 'Te hoge snelheid in lagen met padobjecten kan resulteren in lagere precisie bij snijden.\nWe raden geen snelheid hoger dan %(limit)s aan bij snijden.',
      too_fast_for_path_and_constrain: 'De volgende lagen: %(layers)s \nbevatten vector padobjecten, en hebben een snelheid hoger dan %(limit)s.\nDe snijsnelheid van vector padobjecten wordt beperkt tot %(limit)s.\nU kunt deze beperking opheffen bij de voorkeuren.',
      touchpad: 'Touchpad',
      upload_failed: '#819 Uploaden van bestand mislukt.',
      upload_file_too_large: '#819 Bestand is te groot om te uploaden.',
      vectorize_shading_image: 'Gradientafbeeldingen vergen meer verwerkingstijd voor vectorisatie en zijn gevoelig voor ruis. Schakel de afbeeldingsgradiënt uit voordat u verdergaat.',
    },
    rating_panel: {
      description: 'Als u van Beam Studio houdt, zouden we het zeer op prijs stellen als u ons kunt beoordelen.',
      thank_you: 'Bedankt!',
      title: 'Geniet u van Beam Studio?',
    },
    right_panel: {
      laser_panel: {
        advanced: 'Geavanceerd',
        air_assist: 'Luchtondersteuning',
        am_density: 'Rasterpuntdichtheid',
        backlash: 'Speling',
        bi_directional: 'Tweerichtingsverkeer',
        by: 'Met',
        ce_z_high_speed: 'Hogesnelheids-Z-as',
        ce_z_high_speed_link: 'https://support.flux3dp.com/hc/en-us/articles/10364060644495',
        color_adjustment: 'Kanaal aanpassing',
        color_adjustment_short: 'Kanaal',
        color_strength: 'Kracht',
        cross_hatch: 'Kruislings Arceren',
        custom_preset: 'Aangepast',
        cut: 'Snijden',
        diode: 'Diode laser',
        do_not_adjust_default_para: 'Standaardinstellingen kunnen niet worden aangepast.',
        dottingTime: 'Puntentijd',
        dropdown: {
          inches: {
            abs_engraving: 'ABS - Graveren',
            acrylic_3mm_cutting: 'Acryl - 1/8″ Snijden',
            acrylic_5mm_cutting: 'Acryl - 3/16″ Snijden',
            acrylic_7mm_cutting: 'Acryl - 1/4″ Snijden',
            acrylic_8mm_cutting: 'Acryl - 1/4″ Snijden',
            acrylic_10mm_cutting: 'Acryl - 3/8″ Snijden',
            acrylic_engraving: 'Acryl - Graveren',
            acrylic_printing: 'Acryl - Printen',
            aluminum_engraving: 'Aluminium - Graveren',
            aluminum_light: 'Aluminium (licht)',
            bamboo_2mm_cutting: 'Bamboe - 5/64″ Snijden',
            bamboo_5mm_cutting: 'Bamboe - 3/16″ Snijden',
            bamboo_printing: 'Bamboe - Printen',
            black_abs: 'Zwart ABS',
            black_acrylic_3mm_cutting: 'Zwart acryl - 1/8″ Snijden',
            black_acrylic_5mm_cutting: 'Zwart acryl - 3/16″ Snijden',
            black_acrylic_engraving: 'Zwart acryl - Graveren',
            brass_dark: 'Messing (donker)',
            brass_engraving: 'Messing - Graveren',
            brass_light: 'Messing (licht)',
            canvas_fabric_printing: 'Canvasstof - Printen',
            canvas_printing: 'Canvas - Printen',
            cardboard_printing: 'Karton - Printen',
            cardstock_printing: 'Karton - Printen',
            copper: 'Koper',
            cork_printing: 'Kurk - Printen',
            denim_1mm_cutting: 'Denim - 1/32″ Snijden',
            fabric_3mm_cutting: 'Textiel - 1/8″ Snijden',
            fabric_5mm_cutting: 'Textiel - 3/16″ Snijden',
            fabric_engraving: 'Textiel - Graveren',
            fabric_printing: 'Textiel - Printen',
            flat_stone_printing: 'Platte steen - Printen',
            glass_bw_engraving: 'Glas - Graveren',
            glass_printing: 'Glas - Printen',
            gloss_leather_printing: 'Glans leer - Printen',
            gold_engraving: 'Goud - Graveren',
            iron_engraving: 'IJzer - Graveren',
            leather_3mm_cutting: 'Leer - 1/8″ Snijden',
            leather_5mm_cutting: 'Leer - 3/16″ Snijden',
            leather_engraving: 'Leer - Graveren',
            leather_printing: 'Leer - Printen',
            mdf_3mm_cutting: 'New Zealand MDF - 1/8″ Snijden',
            mdf_5mm_cutting: 'New Zealand MDF - 3/16″ Snijden',
            mdf_7mm_cutting: 'New Zealand MDF - 1/4″ Snijden',
            mdf_engraving: 'New Zealand MDF - Graveren',
            mdf_printing: 'New Zealand MDF - Printen',
            metal_bw_engraving: 'Metaal - Graveren',
            opaque_acrylic: 'Ondoorzichtig acryl',
            pc_printing: 'PC - Printen',
            rubber_bw_engraving: 'Rubber - Graveren',
            silver_engraving: 'Zilver - Graveren',
            slate_engraving: 'Leisteen - Graveren',
            stainless_steel_bw_engraving_diode: 'RVS - Graveren (diodelaser)',
            stainless_steel_dark: 'Roestvrij staal (donker)',
            stainless_steel_engraving: 'RVS - Graveren',
            stainless_steel_light: 'Roestvrij staal (licht)',
            stainless_steel_printing: 'RVS - Printen',
            steel_engraving_spray_engraving: 'Metaal - Graveren',
            sticker_kiss_cut: 'Sticker – contoursnede tot drager',
            sticker_printing: 'Sticker - Printen',
            stone: 'Steen',
            ti_engraving: 'Titanium - Graveren',
            titanium_dark: 'Titanium (donker)',
            titanium_light: 'Titanium (licht)',
            white_abs: 'Wit ABS',
            wood_3mm_cutting: 'Hout - 1/8″ Snijden',
            wood_5mm_cutting: 'Hout - 3/16″ Snijden',
            wood_7mm_cutting: 'Hout - 1/4″ Snijden',
            wood_8mm_cutting: 'Hout - 1/4″ Snijden',
            wood_10mm_cutting: 'Hout - 3/8″ Snijden',
            wood_engraving: 'Hout - Graveren',
            wood_printing: 'Hout - Printen',
          },
          mm: {
            abs_engraving: 'ABS - Graveren',
            acrylic_3mm_cutting: 'Acryl - 3mm Snijden',
            acrylic_5mm_cutting: 'Acryl - 5mm Snijden',
            acrylic_7mm_cutting: 'Acryl - 7mm Snijden',
            acrylic_8mm_cutting: 'Acryl - 8mm Snijden',
            acrylic_10mm_cutting: 'Acryl - 10mm Snijden',
            acrylic_engraving: 'Acryl - Graveren',
            acrylic_printing: 'Acryl - Printen',
            aluminum_engraving: 'Aluminium - Graveren',
            aluminum_light: 'Aluminium (licht)',
            bamboo_2mm_cutting: 'Bamboe - 2mm Snijden',
            bamboo_5mm_cutting: 'Bamboe - 5mm Snijden',
            bamboo_printing: 'Bamboe - Printen',
            black_abs: 'Zwart ABS',
            black_acrylic_3mm_cutting: 'Zwart Acryl - 3mm Snijden',
            black_acrylic_5mm_cutting: 'Zwart Acryl - 5mm Snijden',
            black_acrylic_engraving: 'Zwart Acryl - Graveren',
            brass_dark: 'Messing (donker)',
            brass_engraving: 'Messing - Graveren',
            brass_light: 'Messing (licht)',
            canvas_fabric_printing: 'Canvasstof - Printen',
            canvas_printing: 'Canvas - Printen',
            cardboard_printing: 'Karton - Printen',
            cardstock_printing: 'Karton - Printen',
            copper: 'Koper',
            cork_printing: 'Kurk - Printen',
            denim_1mm_cutting: 'Denim - 1mm Snijden',
            fabric_3mm_cutting: 'Stof - 3mm Snijden',
            fabric_5mm_cutting: 'Stof - 5mm Snijden',
            fabric_engraving: 'Stof - Graveren',
            fabric_printing: 'Stof - Printen',
            flat_stone_printing: 'Platte steen - Printen',
            glass_bw_engraving: 'Glas - Graveren',
            glass_printing: 'Glas - Printen',
            gloss_leather_printing: 'Glansleer - Printen',
            gold_engraving: 'Goud - Graveren',
            iron_engraving: 'IJzer - Graveren',
            leather_3mm_cutting: 'Leer - 3mm Snijden',
            leather_5mm_cutting: 'Leer - 5mm Snijden',
            leather_engraving: 'Leer - Graveren',
            leather_printing: 'Leer - Printen',
            mdf_3mm_cutting: 'New Zealand MDF - 3mm Snijden',
            mdf_5mm_cutting: 'New Zealand MDF - 5mm Snijden',
            mdf_7mm_cutting: 'New Zealand MDF - 7mm Snijden',
            mdf_engraving: 'New Zealand MDF - Graveren',
            mdf_printing: 'New Zealand MDF - Printen',
            metal_bw_engraving: 'Metaal - Graveren',
            opaque_acrylic: 'Ondoorzichtig acryl',
            pc_printing: 'PC - Printen',
            rubber_bw_engraving: 'Rubber - Graveren',
            silver_engraving: 'Zilver - Graveren',
            slate_engraving: 'Leisteen - Graveren',
            stainless_steel_bw_engraving_diode: 'Metaal - Graveren (Diode Laser)',
            stainless_steel_dark: 'Roestvrij staal (donker)',
            stainless_steel_engraving: 'Roestvrij Staal - Graveren',
            stainless_steel_light: 'Roestvrij staal (licht)',
            stainless_steel_printing: 'Roestvrij staal - Printen',
            steel_engraving_spray_engraving: 'Metaal - Graveren',
            sticker_kiss_cut: 'Sticker – contoursnede tot drager',
            sticker_printing: 'Sticker - Printen',
            stone: 'Steen',
            ti_engraving: 'Titaan - Graveren',
            titanium_dark: 'Titanium (donker)',
            titanium_light: 'Titanium (licht)',
            white_abs: 'Wit ABS',
            wood_3mm_cutting: 'Hout - 3mm Snijden',
            wood_5mm_cutting: 'Hout - 5mm Snijden',
            wood_7mm_cutting: 'Hout - 7mm Snijden',
            wood_8mm_cutting: 'Hout - 8mm Snijden',
            wood_10mm_cutting: 'Hout - 10mm Snijden',
            wood_engraving: 'Hout - Graveren',
            wood_printing: 'Hout - Printen',
          },
          parameters: 'Voorinstellingen',
          save: 'Voeg huidige parameters toe',
        },
        engrave: 'Graveren',
        existing_name: 'Deze naam is al in gebruik.',
        fill_angle: 'Vulhoek',
        fill_interval: 'Vulinterval',
        fill_setting: 'Vulinstellingen',
        filled_path_only: 'Alleen voor vulpaden',
        focus_adjustment: 'Focusaanpassing',
        frequency: 'Frequentie',
        gradient_only: 'Alleen voor verloopafbeeldingen',
        halftone: 'Halftoon',
        halftone_link: 'https://support.flux3dp.com/hc/en-us/articles/9402670389647',
        height: 'Objecthoogte',
        high_quality: 'Hoge kwaliteit',
        high_quality_desc: 'Alleen van toepassing op gevulde vectorgraveringen en afbeeldingsobjecten. Het inschakelen hiervan vermindert de graveersversnelling voor fijnere resultaten.',
        ink_saturation: 'Verzadiging',
        low_air_assist_warning: 'Het wordt aanbevolen om de luchttoevoer boven de 50% in te stellen om het risico op brand van het materiaal effectief te verminderen.',
        low_power_warning: 'Lager laservermogen (onder 10%) zou geen laserlicht kunnen uitstralen.',
        low_speed_warning: 'Lage snelheid kan brandplekken op het materiaal veroorzaken.',
        lower_focus: 'Focus Verlagen',
        lower_focus_desc: 'Verlagen van de focushoogte met een specifieke afstand na het focussen om de snijprestaties te verbeteren.',
        module: 'Module',
        more: 'Beheren',
        multi_layer: 'Meerdere lagen',
        para_in_use: 'Deze parameter wordt gebruikt.',
        parameters: 'Parameters',
        preset_management: {
          add_new: 'Nieuwe Toevoegen',
          delete: 'Verwijderen',
          export: 'Exporteren',
          export_preset_title: 'Voorinstellingen exporteren',
          import: 'Importeren',
          laser: 'Laser',
          lower_focus_by: 'Verlaag Focus met',
          new_preset_name: 'Nieuwe voorinstelling naam',
          preset: 'Voorinstelling',
          print: 'Afdrukken',
          reset: 'Resetten',
          save_and_exit: 'Opslaan en afsluiten',
          show_all: 'Alles Weergeven',
          sure_to_import_presets: 'Hierdoor wordt de indeling van de voorinstellingen geladen en worden aangepaste parameters overschreven. Weet u zeker dat u wilt doorgaan?',
          sure_to_reset: 'Hierdoor worden uw aangepaste parameters verwijderd en worden alle voorinstellingen opnieuw ingesteld. Weet u zeker dat u wilt doorgaan?',
          title: 'Beheer Parameters',
          wobble_diameter: 'Wobble-diameter',
          wobble_step: 'Wobble-stapgrootte',
        },
        preset_setting: 'Parameterinstellingen (%s)',
        presets: 'Voorinstelling',
        print_multipass: 'Meervoudig',
        promark_speed_desc: 'Snelheidsparameters zijn niet van toepassing op afbeeldingen met verlopen.',
        pulse_width: 'Pulsbreedte',
        pwm_advanced_desc: 'Stel een minimaal vermogen in voor dieptemodus.',
        pwm_advanced_hint: 'Deze instelling is specifiek van toepassing op gradiëntafbeeldingen bij gebruik van dieptemodus.',
        pwm_advanced_setting: 'Stroominstellingen voor Dieptemodus',
        refresh_interval: 'Verversingsinterval',
        refresh_printer: 'Printkop verversing',
        repeat: 'Aantal Passes',
        single_color: 'Enkele Kleur',
        single_color_desc: 'Alleen van toepassing op volledige kleurenlaag en kan niet worden gebruikt met uitgebreide enkele kleurenlaag.',
        slider: {
          fast: 'Snel',
          high: 'Hoog',
          low: 'Laag',
          regular: 'Normaal',
          slow: 'Zeer langzaam',
          very_fast: 'Zeer snel',
          very_high: 'Maximaal',
          very_low: 'Minimaal',
          very_slow: 'Langzaamst',
        },
        speed: 'Snelheid',
        speed_constrain_warning: 'De snijsnelheid van vectorpadobjecten wordt beperkt tot %(limit)s. U kunt deze beperking opheffen bij de voorkeursinstellingen.',
        speed_constrain_warning_auto_feeder: 'De snijsnelheid van Auto Feeder-objecten wordt beperkt tot %(limit)s. U kunt deze limiet verwijderen in de Voorkeureninstellingen.',
        speed_constrain_warning_curve_engraving: 'De snelheid van gebogen graveerobjecten wordt beperkt tot %(limit)s. Je kunt deze limiet verwijderen in de voorkeureninstellingen.',
        speed_constrain_warning_module_addon: 'Vermijd bij gebruik van een extra module snelheden boven %(limit)s om de graveerkwaliteit te behouden.',
        stepwise_focusing: 'Stapsgewijs Focussen',
        stepwise_focusing_desc: 'Het geleidelijk verlagen van de focusafstand op basis van de hoogte van het object tijdens elk aantal passes.',
        strength: 'Vermogen',
        times: 'keer',
        various_preset: 'Diverse voorinstellingen',
        white_ink: 'Witte inkt',
        white_ink_settings: 'Witte inkt instellingen',
        wobble: 'Wiebelen',
        wobble_desc: 'Creëert kleine cirkels langs het pad, wat voordelig is voor snijtaken. (Alleen voor padobjecten.)',
        wobble_diameter: 'Diameter',
        wobble_step: 'Stapgrootte',
        z_step: 'Z-stap',
      },
      layer_panel: {
        current_layer: 'Huidige laag',
        layer1: 'Laag 1',
        layer_bitmap: 'Bitmap',
        layer_cutting: 'Snijden',
        layer_engraving: 'Graveren',
        layers: {
          del: 'Laag verwijderen',
          dupe: 'Laag dupliceren',
          fullColor: 'Volledige kleur',
          layer: 'Laag',
          layers: 'Lagen',
          lock: 'Laag vergrendelen',
          merge_all: 'Alles samenvoegen',
          merge_down: 'Samenvoegen omlaag',
          merge_selected: 'Geselecteerde lagen samenvoegen',
          move_down: 'Laag omlaag verplaatsen',
          move_selected: 'Geselecteerde elementen verplaatsen naar een andere laag',
          move_up: 'Laag omhoog verplaatsen',
          new: 'Nieuwe laag',
          rename: 'Laag hernoemen',
          splitFullColor: 'Laag splitsen',
          switchToFullColor: 'Overschakelen naar volledige kleurenlaag',
          switchToSingleColor: 'Overschakelen naar enkele kleurenlaag',
          unlock: 'Ontgrendelen',
        },
        move_elems_to: 'Verplaats elementen naar:',
        notification: {
          dupeLayerName: 'Er is al een laag met die naam!',
          enterNewLayerName: 'Voer de nieuwe laagnaam in',
          enterUniqueLayerName: 'Voer een unieke laagnaam in',
          layerHasThatName: 'Laag heeft al die naam',
          mergeLaserLayerToPrintingLayerMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de instellingen van de laserlaag worden verwijderd en ingesteld volgens de huidige laag.',
          mergeLaserLayerToPrintingLayerTitle: 'Wilt u deze lagen samenvoegen tot één printlaag?',
          mergePrintingLayerToLaserLayerMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de kleurinstellingen van de printlaag worden verwijderd en ingesteld volgens de huidige laag.',
          mergePrintingLayerToLaserLayerTitle: 'Wilt u deze lagen samenvoegen tot één laserlaag?',
          moveElemFromPrintingLayerMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de kleurinstellingen van het geselecteerde element worden verwijderd en ingesteld volgens %s.',
          moveElemFromPrintingLayerTitle: 'Wilt u het geselecteerde element verplaatsen naar %s en converteren naar een laser element?',
          moveElemToPrintingLayerMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de instellingen van het geselecteerde element worden verwijderd en ingesteld volgens %s.',
          moveElemToPrintingLayerTitle: 'Wilt u het geselecteerde element verplaatsen naar %s en converteren naar een print element?',
          newName: 'NIEUWE NAAM',
          QmoveElemsToLayer: "Geselecteerde elementen verplaatsen naar laag '%s'?",
          splitColorMsg: 'Houd er rekening mee dat u, als u doorgaat met deze bewerking, niet meer kunt terugkeren naar de oorspronkelijke kleurlagen.',
          splitColorTitle: 'Wilt u de geselecteerde laag uitbreiden naar CMYK-lagen?',
        },
      },
      object_panel: {
        actions_panel: {
          ai_bg_removal: 'Achtergrond verwijderen',
          ai_bg_removal_reminder: 'Druk op de knop gebruikt direct 0,02 tegoed, wilt u doorgaan?',
          ai_bg_removal_short: 'Achtergrondverw.',
          array: 'Array',
          auto_fit: 'Auto Aanpassen',
          bevel: 'Afronden',
          brightness: 'Helderheid',
          create_textpath: 'Tekst op pad maken',
          create_textpath_short: 'Padtekst',
          crop: 'Bijsnijden',
          decompose_path: 'Ontleden',
          detach_path: 'Tekst van pad scheiden',
          detach_path_short: 'Scheiden',
          disabled_by_gradient: 'Schakel de verloopfunctie uit voordat u deze functie gebruikt.',
          disabled_by_infilled: 'Sluit de vulling voordat u deze functie gebruikt.',
          disabled_by_infilled_and_variable_text: 'Sluit de vulling en de variabele tekst voordat u deze functie gebruikt.',
          disabled_by_variable_text: 'Sluit de variabele tekst voordat u deze functie gebruikt.',
          disassemble_use: 'Demonteren',
          disassembling: 'Aan het demonteren...',
          edit_path: 'Pad bewerken',
          fetching_web_font: 'Online lettertype ophalen...',
          grading: 'Graderen',
          invert: 'Inverteren',
          offset: 'Verschuiven',
          outline: 'Omtrek',
          replace_with: 'Vervangen door...',
          replace_with_short: 'Vervangen',
          sharpen: 'Scherper maken',
          simplify: 'Vereenvoudigen',
          smart_nest: 'Slim Nestelen',
          to_image: 'Naar afbeelding',
          to_path: 'Naar pad',
          trace: 'Natrekken',
          ungrouping: 'Groepering opheffen...',
          uploading_font_to_machine: 'Lettertype uploaden naar machine...',
          wait_for_parsing_font: 'Lettertype aan het parseren... Een moment geduld',
          weld_text: 'Tekst lassen',
        },
        align: 'Uitlijnen',
        boolean: 'Booleaans',
        bottom_align: 'Onder uitlijnen',
        center_align: 'Centreren',
        difference: 'Verschil',
        distribute: 'Verdeel',
        flip: 'Spiegelen',
        group: 'Groep',
        hdist: 'Horizontaal verdelen',
        hflip: 'Horizontaal spiegelen',
        intersect: 'Snijden',
        left_align: 'Links uitlijnen',
        lock_aspect: 'Aspect vergrendelen',
        middle_align: 'Midden uitlijnen',
        option_panel: {
          color: 'Kleur',
          current_time: 'Huidige tijd',
          depth_engraving: 'Reliëf',
          fill: 'Vulling',
          font_family: 'Lettertype',
          font_size: 'Grootte',
          font_style: 'Stijl',
          layer_count: 'Aantal lagen',
          letter_spacing: 'Letterspatiëring',
          line_spacing: 'Regelafstand',
          number: 'Volgnummer',
          offset: 'Verschuiving',
          path_infill: 'Padvulling',
          pwm_engraving: 'Dieptemodus',
          pwm_engraving_link: 'https://support.flux3dp.com/hc/en-us/articles/10419884701327',
          recently_used: 'Recent gebruikt',
          rounded_corner: 'Afgeronde hoek',
          shading: 'Verloop',
          sides: 'Zijden',
          start_offset: 'Tekstverschuiving',
          stroke: 'Lijn',
          stroke_color: 'Lijnkleur',
          stroke_width: 'Lijndikte',
          text_align_center: 'Centreren',
          text_align_justify: 'Uitvullen',
          text_align_left: 'Links',
          text_align_right: 'Rechts',
          text_content_placeholder: 'Tekst invoeren',
          text_infill: 'Tekstvulling',
          threshold: 'Drempelhelderheid',
          threshold_short: 'Drempel',
          variable_text: 'Variabele tekst',
          variable_text_link: 'https://support.flux3dp.com/hc/en-us/articles/12689396604175',
          vertical_align: 'Uitlijning',
          vertical_text: 'Verticaal tekst',
        },
        path_edit_panel: {
          connect: 'Verbinden',
          delete: 'Verwijderen',
          disconnect: 'Ontkoppelen',
          node_type: 'Knooppunttype',
          round: 'Rond',
          sharp: 'Scherp',
        },
        right_align: 'Rechts uitlijnen',
        subtract: 'Aftrekken',
        top_align: 'Boven uitlijnen',
        ungroup: 'Degroepeer',
        union: 'Verenigen',
        unlock_aspect: 'Aspect ontgrendelen',
        vdist: 'Verticaal verdelen',
        vflip: 'Verticaal omdraaien',
        zoom: 'Zoom',
      },
      uv_print_block: {
        title: 'UV-print',
      },
    },
    svg_editor: {
      unable_to_fetch_clipboard_img: 'Het ophalen van de afbeelding uit het klembord is mislukt',
      unnsupported_file_type: 'Het bestandstype wordt niet direct ondersteund. Converteer het bestand naar SVG of bitmap',
    },
    tag: {
      g: 'Groep',
      image: 'Afbeelding',
      text: 'Tekst',
      use: 'Importeer SVG',
    },
    tool_panels: {
      _nest: {
        end: 'Sluiten',
        no_element: 'Er zijn geen elementen om in te pakken.',
        rotations: 'Mogelijke rotaties',
        spacing: 'Tussenruimte',
        start_nest: 'Inpakken',
        stop_nest: 'Stoppen',
      },
      _offset: {
        corner_type: 'Hoek',
        direction: 'Verschuivingsrichting',
        dist: 'Verschuivingsafstand',
        expand: 'Uitbreiden',
        fail_message: 'Kan objecten niet verplaatsen.',
        inward: 'Naar binnen',
        not_support_message: 'Geselecteerde elementen bevatten niet-ondersteunde SVG-tag:\nAfbeelding, Groep, Tekst en Geïmporteerd Object.',
        outward: 'Naar buiten',
        round: 'Rond',
        sharp: 'Scherp',
        shrink: 'Verkleinen',
      },
      array_dimension: 'Rasterafmetingen',
      array_interval: 'Arrayinterval',
      cancel: 'Annuleren',
      columns: 'Kolommen',
      confirm: 'Bevestigen',
      dx: 'X',
      dy: 'Y',
      grid_array: 'Rasterreeks aanmaken',
      nest: 'Nestoptimalisatie',
      offset: 'Verschuiving',
      rows: 'Rijen',
    },
    units: {
      mm: 'mm',
      walt: 'W',
    },
    zoom_block: {
      fit_to_window: 'Passend maken aan venster',
    },
  },
  boxgen: {
    add_option: 'Optie toevoegen',
    basic_box: 'Basisdoos',
    beam_radius: 'Straalcompensatie',
    beam_radius_warning: 'Verwijder de zaagcompensatie wanneer de randen of verbindingen van de doos kort zijn om de doosassemblage te garanderen',
    cancel: 'Annuleren',
    coming_soon: 'Binnenkort beschikbaar',
    continue_import: 'Doorgaan met importeren',
    control_tooltip: 'Linkermuisknop om te draaien\nScrollen om in te zoomen\nRechtermuisknop om te pannen',
    control_tooltip_touch: 'Slepen om te draaien\nKnijpen om in te zoomen\nTwee vingers om te pannen',
    cover: 'Deksel',
    customize: 'Aanpassen',
    depth: 'Diepte',
    edge: 'Rand',
    finger: 'Vinger',
    finger_warning: 'De minimale binnenmaat van de doos moet 6 mm (0,24 inch) zijn om compatibel te zijn met vingerverbindingen.',
    import: 'Importeren',
    inner: 'Binnen',
    joints: 'Verbinding',
    max_dimension_tooltip: 'Max. breedte is %s, max. hoogte/diepte instelling is %s.',
    merge: 'Samenvoegen',
    outer: 'Buiten',
    reset: 'Resetten',
    tCount: 'T Aantal',
    tCount_tooltip: 'Het aantal T-sleuven heeft betrekking op de korte zijde; het aantal aan de lange zijde wordt berekend op basis van de lengte.',
    tDiameter: 'T Diameter',
    text_label: 'Label',
    thickness: 'Dikte',
    title: 'DOOSGEN',
    tLength: 'T Lengte',
    tSlot: 'T-sleuf',
    tSlot_warning: 'De minimale binnenmaat van de doos moet 30 mm (1,18 inch) zijn om compatibel te zijn met T-sleuf verbindingen.',
    volume: 'Volume',
    workarea: 'Werkgebied',
    zoom: 'Zoomen',
  },
  buttons: {
    back: 'Terug',
    back_to_beam_studio: 'Terug naar Beam Studio',
    done: 'Klaar',
    next: 'Volgende',
  },
  calibration: {
    ador_autofocus_focusing_block: 'Druk 3 seconden op het "AF"-pictogram en laat de sonde het scherpstelblok aanraken.',
    ador_autofocus_material: 'Druk 3 seconden op het "AF"-pictogram en laat de sonde het materiaal zachtjes aanraken.',
    align_4_0: 'Linksboven',
    align_4_1: 'Rechtsboven',
    align_4_2: 'Linksonder',
    align_4_3: 'Rechtsonder',
    align_8_0: 'Buitenkant linksboven',
    align_8_1: 'Buitenkant rechtsboven',
    align_8_2: 'Buitenkant linksonder',
    align_8_3: 'Buitenkant rechtsonder',
    align_8_4: 'Binnenkant linksboven',
    align_8_5: 'Binnenkant rechtsboven',
    align_8_6: 'Binnenkant linksonder',
    align_8_7: 'Binnenkant rechtsonder',
    align_bottom_left: 'Linksonder',
    align_bottom_right: 'Rechtsonder',
    align_red_cross_cut: 'Lijn het midden van het rode kruis uit met het snijdende kruis.',
    align_red_cross_print: 'Lijn het midden van het rode kruis uit met het geprinte kruis.',
    align_top_left: 'Linksboven',
    align_top_right: 'Rechtsboven',
    analyze_result_fail: 'Kan vastgelegde afbeelding niet analyseren.<br/>Controleer het volgende:<br/>1. Gefotografeerd papier volledig bedekt met wit papier.<br/>2. Het platform is goed scherpgesteld.',
    ask_for_skip_engraving_task: 'Wilt u de graveerstap overslaan en dan een foto nemen en rechtstreeks kalibreren?',
    ask_for_skip_printing_task: 'Wilt u de afdruktaak overslaan en dan een foto nemen en direct kalibreren?',
    at_focus_height: 'op focushoogte',
    at_lower_height: 'op lagere hoogte',
    back: 'Terug',
    beamo2_autofocus_material: 'Beweeg de laserhandmatig boven het materiaal en laat vervolgens de focussensor zakken om de autofocus te activeren.',
    calculating_camera_matrix: 'Camera matrix berekenen...',
    calculating_regression_parameters: 'Regressieparameters berekenen...',
    calibrate_camera_before_calibrate_modules: 'Voer eerst een camerakalibratie uit voordat u modules kalibreert.',
    calibrate_done: 'Kalibratie voltooid. Betere cameranauwkeurigheid bij precieze scherpstelling.',
    calibrate_done_diode: 'Kalibratie voltooid. Offset van diode lasermodule is opgeslagen.',
    calibrate_success_msg: 'Kalibratie geslaagd.<br/>De kalibratiescore is %s (%.2f).',
    calibrating: 'Kalibreren...',
    calibrating_with_device_pictures: "Kalibreren met apparaatfoto's...",
    camera_calibration: 'Camera-calibratie',
    camera_parameter_saved_successfully: 'Camera parameters succesvol opgeslagen.',
    cancel: 'Annuleren',
    charuco_auto_focus: 'Beweeg de laser boven het patroon en druk op <b>AF</b> om automatisch scherp te stellen.',
    charuco_capture: 'Klik op “Volgende” om het patroon vast te leggen',
    charuco_move_to_point_a: 'Verplaats de laserkop om het rode licht uit te lijnen met het midden van <b>punt A</b>.',
    charuco_move_to_point_b: 'Verplaats de laserkop horizontaal om het rode licht uit te lijnen met het midden van <b>punt B</b>.',
    charuco_place_charuco_horizontal: 'Plaats het kalibratiepatroon horizontaal op de <b>%s</b> van het werkgebied.',
    charuco_place_charuco_vertical: 'Plaats het kalibratiepatroon verticaal op de <b>%s</b> van het werkgebied.',
    charuco_position_bottom_left: 'Linksonder',
    charuco_position_bottom_right: 'Rechtsonder',
    charuco_position_center: 'Midden',
    charuco_position_left: 'Links',
    charuco_position_right: 'Rechts',
    charuco_position_top_left: 'Linksboven',
    charuco_position_top_right: 'Rechtsboven',
    check_checkpoint_data: 'Controlepuntgegevens',
    check_device_pictures: "Apparaatfoto's controleren",
    check_pnp_desc: 'Zorg ervoor dat de rode markeringen zijn uitgelijnd met de graveerpunten\nZo niet, klik op "Terug" om aan te passen.\nAls alles goed is, goed gedaan! 🎉',
    checking_checkpoint: 'Controleer controlepuntgegevens...',
    checking_pictures: "Apparaatfoto's controleren...",
    click_next: 'Klik op "Volgende"',
    click_next_to_take_picture: 'Klik op "Volgende" om een foto te maken.',
    diode_calibration: 'Diode lasermodule-calibratie',
    do_engraving: 'Graveren Utivoeren',
    download_and_print_calibration_pattern: 'Download het kalibratiepatroon hieronder en print het op A4- of briefformaat papier.',
    download_calibration_pattern: 'Kalibratiepatroon Downloaden',
    download_chessboard_file: 'Download schaakbord bestand',
    downloading_checkpoint: 'Downloaden van controlepuntgegevens...',
    downloading_pictures: "Foto's downloaden...",
    drawing_calibration_image: 'Kalibratieafbeelding tekenen...',
    dx: 'X',
    dy: 'Y',
    elevate_and_cut: 'Optillen en Snijden',
    elevate_and_cut_step_1: 'Plaats een A4-formaat lichtgekleurd hout in het midden van de werkruimte en verhoog het tot 20mm.',
    elevate_and_cut_step_1_prism_lift: 'Gebruik Ador Prism Lift met een maximale lengte van 14mm samen met een houten plank van minstens 6mm dikte om het hout tot een hoogte van 20mm te verhogen.',
    failed_to_calibrate_camera: '#848 Fout bij camera kalibratie, neem contact op met FLUX Support.',
    failed_to_calibrate_chessboard: 'Mislukt kalibreren met het schaakbordbeeld.',
    failed_to_calibrate_with_pictures: "#848 Kalibratie met apparaatfoto's mislukt.",
    failed_to_detect_calibration_pattern: 'Het kalibratiepatroon kon niet worden gedetecteerd. Pas de belichting van de camera aan of verplaats het patroon en probeer het opnieuw.',
    failed_to_download_pictures: "#848 Fout bij downloaden foto's, neem contact op met FLUX Support.",
    failed_to_move_laser_head: 'Kon de laserhoofdkop niet verplaatsen.',
    failed_to_parse_checkpoint: 'Kan de controlepuntgegevens niet analyseren.',
    failed_to_save_calibration_results: '#849 Fout bij opslaan kalibratieresultaten, probeer opnieuw. Neem contact op met FLUX Support als dit blijft gebeuren.',
    failed_to_save_camera_parameter: 'Opslaan van camera parameters mislukt.',
    failed_to_solve_pnp: 'Het lukte niet om de camerapositie op te lossen.',
    finish: 'Klaar',
    found_checkpoint: 'Controlepuntgegevens gevonden op uw apparaat. Wilt u herstellen vanuit het controlepunt?',
    getting_plane_height: 'Hoogte van het vlak ophalen...',
    hint_adjust_parameters: 'Gebruik deze parameters om het rode vierkant aan te passen',
    hint_red_square: 'Lijn de buitenkant van het rode vierkant uit met het gesneden vierkant.',
    material_4_a4_paper: 'Vier vellen wit papier in A4- of briefformaat.',
    material_a4_calibration_pattern: 'Kalibratiepatroon in A4- of briefformaat.',
    materials_required: 'Benodigde materialen:',
    module_calibration_2w_ir: 'Infraroodmodule-calibratie',
    module_calibration_printer: 'Printermodule-calibratie',
    module_offset_settings: 'Offset-instellingen',
    module_offset_settings_desc: 'Pas de X- en Y-offsetwaarden aan om de werkelijke %s positie uit te lijnen met de CO2-laseruitvoer.',
    module_offset_settings_save_failed: 'Kan offset-instellingen niet opslaan.',
    moving_laser_head: 'De laserhoofdkop verplaatsen...',
    moving_platform: 'Platform wordt verplaatst...',
    next: 'Volgende',
    no_picture_found: '#852 Uw apparaat heeft geen beschikbare ruwe foto\'s voor kalibratie. Voer alstublieft "Camera kalibreren (Geavanceerd)" uit.',
    perform_autofocus_bb2: 'Ga naar het bedieningspaneel van de machine en druk op AF om de autofocus uit te voeren.',
    perform_autofocus_bm2: 'Verplaats de laserhandmatig boven het materiaal en laat vervolgens de focusprobe zakken om autofocus te activeren.',
    please_do_camera_calibration_and_focus: {
      beambox: 'Wanneer u de diode lasermodule kalibreert, is een camera nodig.\nZorg ervoor dat de camera van deze machine is gekalibreerd.\nEn pas het platform vriendelijk aan op het brandpunt (de hoogte van het omgekeerde acryl)',
      beamo: 'Wanneer u de diode lasermodule kalibreert, is een camera nodig.\nZorg ervoor dat de camera van deze machine is gekalibreerd.\nEn pas de laserkop vriendelijk aan om scherp te stellen op het graveerobject (de hoogte van het omgekeerde acryl)',
    },
    please_goto_editor_first: 'Voltooi de installatie voordat u verdergaat',
    please_place_dark_colored_paper: 'Plaats een A4- of briefpapierformaat donkergekleurd papier in het midden van het werkgebied.',
    please_place_paper: 'Plaats een A4- of briefpapierformaat wit papier linksboven in het werkgebied.',
    please_place_paper_center: 'Plaats een A4- of briefpapierformaat wit papier in het midden van het werkgebied.',
    please_refocus: {
      beambox: 'Stel het platform af op de brandpuntsafstand (de hoogte van de omlaag gedraaide acryl).',
      beamo: 'Stel de laserkop af om scherp te stellen op het graveerobject (de hoogte van de omlaag gedraaide acryl).',
      beamo_af: 'Dubbeltik de zijknop van de autofocus-accessoire en laat de sonde het materiaal zachtjes aanraken.',
      hexa: 'Dubbelklik op de hoogteverstelknop om de honingraattabel omhoog te brengen en de sonde het graveermateriaal te laten aanraken.',
    },
    preparing_to_take_picture: 'Voorbereiden om foto te nemen...',
    promark_help_link: 'https://support.flux3dp.com/hc/en-us/articles/11173605809295',
    put_charuco: 'Kalibratiepatroon Plaatsen',
    put_charuco_1: 'Print het kalibratiepatroon (A4/Letter).',
    put_charuco_2: 'Plak het plat op een stijve plaat.',
    put_charuco_3: 'Plaats in het midden en voer autofocus uit.',
    put_charuco_bm2_1: 'Print het kalibratiepatroonbestand op A4- (Letterformaat) papier voor kalibratie.',
    put_charuco_bm2_2: 'Klik op “Volgende” en volg de gids om het papier te plaatsen.',
    put_charuco_promark_2: "Zorg ervoor dat het kalibratiepapier duidelijk zichtbaar is zonder schittering. Klik op 'Volgende' om een foto te maken.",
    put_charuco_promark_desc_1: 'Gebruik het kalibratiepapier uit de accessoiredoos of print het volgende kalibratiepatroon op A4‑papier voor camerakalibratie.',
    put_charuco_promark_desc_2: 'Plaats het kalibratiepapier plat en gecentreerd op het werkplatform.',
    put_chessboard_promark_1: 'Pas de brandpuntsafstand aan naar de juiste focus voor de veldlens.',
    put_chessboard_promark_2: 'Zorg ervoor dat het schaakbordpapier duidelijk zichtbaar is zonder schittering. Klik op "Volgende" om een foto te maken.',
    put_chessboard_promark_desc_1: 'Gebruik het schaakbordpapier dat in de accessoiredoos is meegeleverd, of druk het volgende schaakbordpatroon af op A4-papier voor camerakalibratie.',
    put_chessboard_promark_desc_2: 'Plaats het schaakbord plat en gecentreerd op het werkplatform.',
    put_paper: 'Plaats papier',
    put_paper_bm2_1: 'Leg twee witte A4-vellen plat neer en bevestig ze.',
    put_paper_bm2_2: 'Verlaag de focusonde om automatisch te focussen op het papier.',
    put_paper_bm2_3: 'Sluit het deurdeksel.',
    put_paper_bm2_4: 'Klik op "Graveren Starten".',
    put_paper_promark: 'Plaats zwart karton',
    put_paper_promark_1: 'Plaats het zwarte karton uit de accessoiredoos op het werkplatform.',
    put_paper_promark_2: 'Stel de focus correct af en klik vervolgens op "Graveren Starten" om verder te gaan.',
    put_paper_skip: 'Als de kalibratietekening niet automatisch doorgaat naar het maken van een foto, druk dan op "Overslaan" om een foto te maken.',
    put_paper_step1: 'Plaats alstublieft een A4- of Letter-formaat wit papier in het midden van het werkgebied.',
    put_paper_step2: 'Bevestig de vier hoeken van het papier om ervoor te zorgen dat het plat ligt.',
    put_paper_step3: 'Klik op "Graveren Starten".',
    put_paper_wide_angle_1: 'Plaats vier witte A4- of Letter-formaat papieren plat om het gehele werkgebied te bedekken.',
    put_paper_wide_angle_2: 'Bevestig de papieren zodat ze plat blijven liggen.',
    relocate_camera: 'Camera opnieuw positioneren',
    relocate_camera_desc: 'Als de deur wordt geopend of de laserkop wordt verplaatst, herplaats dan de camera en lijn de punten opnieuw uit.',
    res_average: 'Gemiddeld',
    res_excellent: 'Uitstekend',
    res_poor: 'Slecht',
    reset_points: 'Punten resetten',
    retake: 'Foto opnieuw nemen',
    rotation_angle: 'Rotatie',
    show_last_config: 'Laatste resultaat weergeven',
    skip: 'Overslaan',
    solve_pnp_bm2: 'Bevestig dat 16 markeringen op de papieren zijn gegraveerd. (Zo niet, klik op Terug om opnieuw te graveren.)',
    solve_pnp_keep_door_closed: 'Houd de deur gesloten tijdens het volgende proces.',
    solve_pnp_move_platform: 'De machine zal automatisch naar beneden bewegen en een andere foto nemen. Verplaats het kalibratiepapier niet tijdens dit proces.',
    solve_pnp_open_the_lid: 'Open alstublieft het deksel om de foto te maken.',
    solve_pnp_step1: 'Lijn de graveerpunten uit volgens het nummer en de verwachte positie van elk rood markeerpunt.',
    solve_pnp_step2: 'U kunt op "Foto opnieuw nemen" drukken om opnieuw uit te lijnen of de posities van de markeringen handmatig aan te passen.',
    solve_pnp_step3: 'Als het graveerpunt duidelijk verkeerd staat, sleep het beeld totdat het punt zichtbaar is en klik op "Foto opnieuw maken".',
    solve_pnp_title: 'Markeringspunten uitlijnen',
    start_engrave: 'Graveren Starten',
    start_printing: 'Afdrukken Starten',
    step: 'Stap',
    taking_picture: 'Foto maken...',
    title_align_marker_points: 'Markeerpunten uitlijnen',
    title_capture_calibration_pattern: 'Kalibratiepatroon vastleggen',
    title_confirm_calibration_result: 'Kalibratieresultaat bevestigen',
    title_engrave_marker_points: 'Markeerpunten graveren',
    title_prepare_materials: 'Materialen voorbereiden',
    unable_to_load_camera_parameters: "#851 Er zijn geen camerakalibratieparameters beschikbaar op uw apparaat. Ga naar 'Kalibratie' > 'Camera kalibreren (Geavanceerd)' om de kalibratie te voltooien en de parameters te verkrijgen.",
    update_firmware_msg1: 'Uw firmware ondersteunt deze functie niet. Werk de firmware bij naar v',
    update_firmware_msg2: 'of hoger om door te gaan。 (Menu > Machine > [Uw machine] > Firmware bijwerken)',
    uploading_images: 'Afbeeldingen uploaden...',
    use_last_config: 'Laatste kalibratiewaarden gebruiken',
    use_old_camera_parameter: 'Wilt u de huidige camerobjectiefparameters gebruiken?',
    with_af: 'Met autofocus',
    with_prism_lift: 'Met Ador Prism Lift',
    with_promark_safe_plus: 'Met Promark Safe+',
    without_af: 'Zonder autofocus',
    without_prism_lift: 'Zonder Ador Prism Lift',
    without_promark_safe_plus: 'Zonder Promark Safe+',
    x_ratio: 'X-verhouding',
    y_ratio: 'Y-verhouding',
    zendesk_link: 'https://support.flux3dp.com/hc/en-us/articles/360001811416',
  },
  camera_data_backup: {
    download_success: 'Camera-gegevens succesvol gedownload.',
    downloading_data: 'Gegevens downloaden...',
    estimated_time_left: 'Geschatte resterende tijd:',
    folder_not_exists: 'Geselecteerde map bestaat niet.',
    incorrect_folder: 'Mislukt om kalibratiegegevens te uploaden. Controleer of de geselecteerde map correct is.',
    no_picture_found: 'Geen afbeelding gevonden in de machine.',
    title: 'Camera gegevens back-up',
    upload_success: 'Camera-gegevens succesvol geüpload.',
    uploading_data: 'Gegevens uploaden...',
  },
  canvas_control: {
    calculate: 'Schat tijd in',
    canvas_zoom: 'Zoom',
    exposure: 'Belichting',
    not_supported: 'Niet ondersteund',
    opacity: 'Dekking',
  },
  caption: {
    connectionTimeout: 'Verbinding time-out',
  },
  change_logs: {
    added: 'Toegevoegd:',
    change_log: 'Wijzigingslogboeken:',
    changed: 'Gewijzigd:',
    fixed: 'Opgelost:',
    help_center_url: 'https://support.flux3dp.com/hc/en-US/sections/360000421876',
    see_older_version: 'Bekijk oudere versies',
  },
  cloud_sync: {
    import_config: 'Cloudinstellingen gevonden voor dit apparaat. Wilt u deze importeren?',
    import_failed: 'Kan cloudinstellingen niet lezen.',
    sync_config: 'Wilt u deze instellingen synchroniseren met de cloud?',
    sync_failed: 'Synchronisatie van instellingen naar de cloud mislukt.',
    sync_success: 'Instellingen succesvol gesynchroniseerd met de cloud.',
  },
  code_generator: {
    barcode: 'Streepjescode',
    content: 'Inhoud',
    import: 'Importeren',
    qr_code: 'QR-code',
    type: 'Type',
  },
  connection_issue_guide: {
    alert_link: 'Verbinding wordt verbroken? Probeer deze probleemoplossingsmethode.',
    connect_computer: {
      content: 'Je computer moet op hetzelfde netwerk zijn als de machine.',
      macos_steps: 'Klik op het Wi-Fi-pictogram in de menubalk. Selecteer vervolgens de hotspot van je telefoon.',
      macos_title: 'macOS',
      title: 'Verbind je computer',
      windows_steps: 'Klik op het netwerkpictogram in de taakbalk. Selecteer vervolgens de hotspot van je telefoon.',
      windows_title: 'Windows',
    },
    connect_wifi: {
      cant_find_hotspot: 'Machine kan mijn hotspot niet vinden',
      dont_know_hotspot_name: 'Ik weet mijn hotspotnaam niet',
      step1: '1. Ga op het touchpaneel van je machine naar: Netwerk → Verbinden met Wi-Fi.',
      step2: '2. Zoek en selecteer de hotspotnaam van je telefoon in de Wi-Fi-lijst.',
      step3: '3. Voer het hotspotwachtwoord in en maak verbinding.',
      title: 'Machine verbinden met telefoonhotspot',
    },
    enter_ip: {
      check_firmware: 'Firmwareversie',
      check_ip_reachable: 'IP bereikbaar',
      check_machine_responds: 'Machine reageert',
      placeholder: 'IP-adres',
      step1: 'Voer het IP-adres in dat je in de vorige stap hebt gevonden.',
      step2: 'Klik op "Verifiëren" om de verbinding te testen.',
      step3: 'Beam Studio controleert:',
      title: 'Voer het IP-adres van je machine in',
      verify: 'Verifiëren',
    },
    fail: {
      bullet1: '• Je machinemodel en serienummer',
      bullet2: '• Het IP-adres dat op het touchpaneel wordt weergegeven',
      bullet3: '• Een beschrijving van de fout die je bent tegengekomen',
      contact_title: 'Wanneer je contact opneemt met de ondersteuning, is het handig om het volgende bij de hand te hebben:',
      intro: 'Geen zorgen — dit betekent niet noodzakelijk dat er iets mis is met je machine. Er kunnen aanvullende factoren zijn die de verbinding beïnvloeden. Ons ondersteuningsteam helpt je graag om het uit te zoeken.',
      title: 'We konden de test niet voltooien',
    },
    hotspot: {
      android_steps: 'Instellingen → Verbindingen → Mobiele hotspot en tethering → Mobiele hotspot',
      android_title: 'Android',
      ios_steps: 'Instellingen → Persoonlijke hotspot → Anderen toestaan deel te nemen',
      ios_title: 'iPhone (iOS)',
      tip: 'Tip: Gebruik een eenvoudige Engelse hotspotnaam en een numeriek wachtwoord (bijv. Naam: FLUX_Test   Wachtwoord: 12345678)',
      warning_2_4ghz: 'Belangrijk: FLUX-machines ondersteunen alleen 2.4GHz Wi-Fi. Stel je hotspot in op 2.4GHz voordat je verdergaat.',
    },
    how_it_works: 'Zo werkt de test:',
    how_it_works_step1: '1. Stel je telefoon in als Wi-Fi-hotspot.',
    how_it_works_step2: '2. Verbind de machine met de hotspot.',
    how_it_works_step3: '3. Verbind je computer met dezelfde hotspot.',
    how_it_works_step4: '4. Test de verbinding in Beam Studio.',
    intro: 'Laten we een snelle test uitvoeren om te controleren of je machine goed werkt. We verbinden je machine en computer via de hotspot van je mobiele telefoon om te bepalen of het probleem bij je netwerk of de machine zelf ligt.',
    ip_address: {
      step1: 'Ga op het touchpaneel van de machine naar:\nNetwerk → Zoek naar "IP-adres"',
      step2: 'De IP ziet er meestal zo uit: 172.20.10.x of 192.168.x.x\n(Dit hangt af van de hotspot van je telefoon)',
      step3: 'Noteer dit IP-adres voor de volgende stap.',
      title: 'Vind het IP-adres van je machine',
      warning: 'Als de IP begint met 169.254, is de verbinding mislukt. Ga terug naar Stap 1 en probeer het opnieuw.',
    },
    success: {
      cause: 'Het verbindingsprobleem dat je hebt ervaren, wordt hoogstwaarschijnlijk veroorzaakt door je lokale netwerkomgeving en niet door de machine zelf.',
      intro: 'Je hebt de Wi-Fi-verbindingstest met succes voltooid — goed gedaan! Dit bevestigt dat je FLUX-machine perfect werkt.',
      recommend1: '• Neem contact op met je internetprovider (ISP) of routerfabrikant voor hulp bij je Wi-Fi-instellingen',
      recommend2: '• Vraag je netwerkbeheerder als je op een school, kantoor of gedeelde werkplek bent',
      recommend3: '• Controleer of je router 2.4GHz ondersteunt en probeer deze opnieuw op te starten',
      recommend_title: 'Wat we aanbevelen:',
      title: 'Goed gedaan! 🎉',
    },
    title: 'Verbindingsprobleem?',
  },
  curve_engraving: {
    '921': 'Automatisch scherpstellen mislukt.',
    '922': 'Meting met rood licht mislukt.',
    amount: 'Hoeveelheid',
    apply_arkwork: 'Kunstwerk toepassen op kromme',
    apply_camera: 'Camerabeeld toepassen op kromme',
    click_to_select_point: 'Klik om punten te selecteren of te deselecteren voor opnieuw meten.',
    coloumns: 'Kolommen',
    column_gap: 'Kolomafstand',
    err_object_over_range: 'Het object overschrijdt het meetbereik.',
    failed_to_take_reference: 'Referentie nemen mislukt',
    gap: 'Opening',
    help_center_url: 'https://support.flux3dp.com/hc/en-us/articles/10364060644495',
    measure_audofocus_area: 'Meet autofocusgebied',
    preview_3d_curve: 'Voorbeeld van kromme graveren',
    remeasure: 'Opnieuw meten',
    remeasuring_points: 'Punten opnieuw meten...',
    reselect_area: 'Selecteer gebied opnieuw',
    row_gap: 'Rijafstand',
    rows: 'Rijen',
    set_object_height: 'Stel objecthoogte in',
    set_object_height_desc: 'Meet de maximale dikte van het object.',
    start_autofocus: 'Start autofocus',
    starting_measurement: 'Meten starten...',
    sure_to_delete: 'Wil je de focusgegevens van de kromme gravering verwijderen?',
    take_reference: 'Referentie nemen',
    take_reference_desc: 'Verplaats de laserkop naar het hoogste punt van het object, laat de focusprobe zakken en klik op "Bevestigen" om te focussen.',
  },
  device: {
    abort: 'Afbreken',
    aborted: 'Afgebroken',
    aborting: 'Wordt afgebroken',
    busy: 'Bezig',
    cartridge_info_read_failed: 'Please ensure that the ink is fully inserted. Try to remove the ink and install it back.',
    cartridge_info_verification_failed: 'Please verify that your ink is FLUX authentic.',
    cartridge_serial_number: 'Ink cartridge serial number',
    close_door_to_read_cartridge_info: "To access ink information, please close the machine's door cover.",
    completed: 'Voltooid',
    completing: 'Voltooien',
    deviceList: 'Apparaatlijst',
    disable: 'Uitschakelen',
    disconnectedError: {
      caption: 'Machine losgekoppeld',
      message: 'Controleer of het netwerk van %s beschikbaar is',
    },
    firmware_version: 'Firmwareversie',
    ink_color: 'Ink color',
    ink_level: 'Ink level',
    ink_type: 'Ink type',
    IP: 'IP',
    model_name: 'Modelnaam',
    occupied: 'Bezet',
    pause: 'Pauzeer',
    paused: 'Gepauzeerd',
    pausedFromError: 'Gepauzeerd vanwege fout',
    pausing: 'Wordt gepauzeerd',
    please_wait: 'Een ogenblik geduld...',
    preparing: 'Voorbereiden',
    processing: 'Verwerken',
    quit: 'Afsluiten',
    ready: 'Klaar',
    reconnecting: 'Opnieuw verbinden',
    reset: 'Resetten (uitzetten)',
    resuming: 'Hervatten',
    retry: 'Opnieuw proberen',
    running: 'Werken',
    scanning: 'Scannen',
    select: 'Selecteren',
    select_printer: 'Selecteer printer',
    serial_number: 'Serienummer',
    start: 'Start',
    starting: 'Opstarten',
    status: 'Status',
    submodule_type: 'Module',
    toolhead_change: 'Gereedschapskop wijzigen',
    unknown: 'Onbekend',
    uploading: 'Uploaden',
    UUID: 'UUID',
  },
  device_selection: {
    go_to_settings: 'Ga Naar Instellingen',
    macos_15_local_network_permission: 'Op macOS 15 of nieuwer, schakel de toegang van Beam Studio tot het lokale netwerk in via Systeeminstellingen > Privacy & Beveiliging > Lokaal netwerk.',
    no_device: '#801 We kunnen uw machine niet vinden in het netwerk.\nVolg de <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/360001683556">handleiding</a> om verbindingsproblemen op te lossen.',
    no_device_web: "#801 Controleer de status van uw machine, of klik op 'Machine-instellingen' hieronder om de machine in te stellen.",
    select_usb_device: 'Selecteer USB-apparaat',
  },
  editor: {
    exposure: 'Helderheid',
    nozzle_refresh_area: 'Mondstuk Verversingsgebied',
    opacity: 'Voorbeeld dekking',
    prespray_area: 'Priming gebied',
  },
  error_pages: {
    screen_size: 'Houd er rekening mee dat Beam Studio mogelijk niet optimaal werkt op uw apparaat. Voor een optimale ervaring moet het een apparaat zijn met een schermbreedte van minimaal 1024 pixels.',
  },
  flux_id_login: {
    connection_fail: '#847 Verbinding met FLUX-lidservice mislukt.',
    email: 'E-mail',
    flux_plus: {
      access_monotype_feature: 'U heeft geen Monotype-lettertypen-invoegtoepassing.',
      access_monotype_feature_note: 'U moet FLUX+ Pro-lid zijn of de Monotype-lettertypen-invoegtoepassing hebben om toegang te krijgen tot deze functie.',
      access_plus_feature_1: 'U heeft toegang tot een',
      access_plus_feature_2: 'functie.',
      access_plus_feature_note: 'U moet FLUX+ lid zijn om toegang te krijgen tot deze functie.',
      ai_credit_tooltip: 'Voor AI-functies',
      explore_plans: 'Verken FLUX+ abonnementen',
      features: {
        ai_bg_removal: 'AI-achtergrondverwijdering',
        boxgen: '3D-doosgenerator',
        dmkt: '1000+ ontwerpbestanden',
        monotype: '250+ premium lettertypen',
        my_cloud: 'Onbeperkte cloudopslag',
      },
      flux_credit_tooltip: 'Voor ontwerpmarktbestanden en AI-achtergrondverwijdering',
      get_addon: 'Krijg invoegtoepassing',
      goto_member_center: 'Ga naar het ledengedeelte',
      learn_more: 'Meer informatie',
      member_center_url: 'https://member.flux3dp.com/en-US/subscription',
      subscribe_now: 'Abonneer nu',
      thank_you: 'Bedankt voor uw lidmaatschap!',
      website_url: 'https://flux3dp.com/subscription',
    },
    forget_password: 'Wachtwoord vergeten?',
    incorrect: 'E-mailadres of wachtwoord is onjuist.',
    login: 'Inloggen',
    login_success: 'Ingelogd',
    lost_password_url: 'https://id.flux3dp.com/user/forgot-password',
    new_to_flux: 'Nieuw bij FLUX? Maak een account.',
    not_verified: 'Het e-mailadres is nog niet geverifieerd.',
    password: 'Wachtwoord',
    register: 'Maak uw FLUX-account',
    remember_me: 'Onthouden',
    sign_in_later: 'Later inloggen',
    signup_url: 'https://id.flux3dp.com/user/login#up',
    unlock_shape_library: 'Log in om de vormendatabase te ontgrendelen.',
  },
  framing: {
    area_check: 'Gebied Controle',
    areacheck_desc: 'Zorgt voor een veilige werkruimte door het begrenzingsvak van het object en de versnellingszone van de laserkop te tonen.',
    calculating_task: 'Taak berekenen...',
    contour: 'Contour',
    contour_desc: 'Bekijk de werkelijke patroonomtrek.',
    framing: 'Inlijsten',
    framing_desc: 'Geeft een voorbeeld van de begrenzingsbox van het object.',
    hull: 'Omhulsel',
    hull_desc: 'Voorvertoont een vorm die nauw aansluit bij het ontwerp, zoals een elastiek dat om het object is gewikkeld.',
    low_laser: 'Lage laser',
    low_laser_desc: 'Stel een laag laservermogen in voor de inlijsttaak.',
    repeat: 'Herhalen',
    rotate_axis: 'Rotatieas',
    rotateaxis_desc: 'Preview het rotatie-as centrum, de as zelf beweegt niet.',
    rotation_frame_warning: 'De framevoorbeeld zal één keer roteren. Stop niet halverwege om verkeerde markeerposities te voorkomen.',
    rotation_framing_desc: 'Preview het volledige rotatiebereik, de as draait volgens de preview.',
    start_task: 'Taak Starten',
    start_task_description: 'Klik op "Taak Starten" na het voltooien van de framevoorbeeld om de taak in te dienen.',
  },
  general: {
    choose_folder: 'Map kiezen',
    processing: 'Verwerken...',
  },
  generators: {
    box_generator: 'Doos Generator',
    code_generator: 'Codegenerator',
    keychain_generator: 'Sleutelhangergenerator',
    material_test_generator: 'Materiaaltestgenerator',
    puzzle_generator: 'Puzzelgenerator',
    title: 'Generatoren',
  },
  generic_error: {
    OPERATION_ERROR: '[OE] Er is een statusconflict opgetreden, probeer de actie opnieuw.',
    SUBSYSTEM_ERROR: '[SE] Fout bij het uitvoeren van de taak door de firmware van de machine. Herstart de machine.',
    UNKNOWN_COMMAND: '[UC] Werk de firmware van het apparaat bij',
    UNKNOWN_ERROR: '[UE] Onbekende fout opgetreden. Herstart Beam Studio en de machine.',
  },
  global: {
    apply: 'Toepassen',
    back: 'Terug',
    cancel: 'Annuleren',
    editing: {
      redo: 'Opnieuw',
      reset: 'Resetten',
      reset_view: 'Weergave Resetten',
      undo: 'Ongedaan maken',
      zoom_in: 'Inzoomen',
      zoom_out: 'Uitzoomen',
    },
    enable: 'Inschakelen',
    height: 'Hoogte',
    mode_conflict: 'Deze optie is niet beschikbaar in de huidige modus.',
    ok: 'OK',
    preview: 'Voorvertoning',
    save: 'Opslaan',
    save_and_exit: 'Opslaan en afsluiten',
    saving: 'Opslaan...',
    select: 'Selecteer',
    stop: 'Stop',
    width: 'Breedte',
  },
  google_font_panel: {
    all_languages: 'Alle talen',
    category: 'Categorie',
    language_label: 'Taal',
    language_placeholder: 'Taal',
    language_unavailable_offline: 'Niet beschikbaar offline',
    more_google_fonts: 'Meer Google Fonts...',
    no_fonts_available: 'Geen lettertypen beschikbaar.',
    no_internet_connection: 'Geen internetverbinding',
    no_results_message: 'Geen lettertypen gevonden die overeenkomen met uw zoekcriteria.',
    no_selection: 'Geen lettertype geselecteerd',
    offline_message: 'Google Fonts zijn niet beschikbaar offline. Maak verbinding met internet om Google Fonts te bekijken en te selecteren.',
    search_placeholder: 'Zoek lettertypen...',
    search_unavailable_offline: 'Zoeken niet beschikbaar offline',
    selected_prefix: 'Geselecteerd:',
    title: 'Google Fonts',
    unavailable_message: 'Google Fonts zijn momenteel niet beschikbaar. Controleer uw internetverbinding of neem contact op met uw beheerder.',
  },
  image_edit_panel: {
    eraser: {
      brush_size: 'Kwastgrootte',
      description: 'Klik of sleep om ongewenste gebieden handmatig te wissen.',
      title: 'Gum',
    },
    magic_wand: {
      description: 'Selecteer en verwijder aangrenzende gebieden op basis van kleurvergelijking.',
      title: 'Toverstok',
      tolerance: 'Tolerantie',
    },
    rounded_corner: {
      description: 'Pas de ronding van de hoeken van het object aan.',
      radius: 'Straal',
      title: 'Afgeronde hoek',
    },
    title: 'Afbeelding Bewerken',
  },
  initialize: {
    back: 'Terug',
    cancel: 'Annuleren',
    confirm: 'Bevestigen',
    connect: 'Verbinden',
    connect_ethernet: {
      title: 'Directe verbinding',
      tutorial1: '1. Verbind de machine met uw computer met een ethernetkabel.',
      tutorial2_1: '2. Volg',
      tutorial2_2: 'om uw computer als router in te stellen.',
      tutorial2_a_href_mac: 'https://support.flux3dp.com/hc/en-us/articles/360001517076',
      tutorial2_a_href_win: 'https://support.flux3dp.com/hc/en-us/articles/360001507715',
      tutorial2_a_text: 'deze handleiding',
      tutorial3: '3. Klik op Volgende.',
    },
    connect_machine_ip: {
      alert: {
        swiftray_connection_error: 'Kan geen verbinding maken met de server. Herstart Beam Studio en probeer het opnieuw.',
      },
      check_camera: 'Camera beschikbaarheid controleren',
      check_connection: 'Machineverbinding controleren',
      check_firmware: 'Firmwareversie controleren',
      check_ip: 'IP-beschikbaarheid controleren',
      check_swiftray_connection: 'Serververbinding controleren',
      check_swiftray_connection_unreachable: 'server niet bereikbaar',
      check_usb: 'Controleer USB-verbinding',
      connection_failed_hint: 'Kan geen verbinding maken met de machine. Raadpleeg <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/360001683556">deze gids</a> om het probleem op te lossen.',
      enter_ip: 'Voer uw machine-IP in',
      finish_setting: 'Begin met ontwerpen!',
      invalid_format: 'Ongeldig formaat',
      invalid_ip: 'Ongeldig IP: ',
      promark_hint: 'Als u herhaaldelijk geen verbinding via USB kunt maken, raadpleeg dan het <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/11318820440591">Helpcentrum-artikel</a>.',
      retry: 'Opnieuw proberen',
      starts_with_169254: 'Begint met 169.254',
      succeeded_message: 'Verbinding gelukt 🎉',
      unreachable: 'IP onbereikbaar',
      unreachable_macos_15: 'Op macOS 15 of later, schakel de toegang van Beam Studio tot het lokale netwerk in via Systeeminstellingen > <a target="_blank" href="x-apple.systempreferences:com.apple.preference.security?Privacy_LocalNetwork">Privacy en beveiliging</a> > Lokaal netwerk.',
    },
    connect_usb: {
      connect_camera: 'Verbind de camera van de machine met uw computer via een USB-kabel.',
      title: 'USB-verbinding',
      title_sub: '(alleen HEXA & Ador)',
      turn_off_machine: 'Zet de machine uit.',
      turn_on_machine: 'Zet de machine aan.',
      tutorial1: 'Sluit de machine aan op uw computer met een USB-kabel.',
      tutorial2: "Klik op 'Volgende'.",
      wait_for_turning_on: "Klik op 'Volgende' nadat het opstartproces is voltooid en u het hoofdscherm hebt bereikt.",
    },
    connect_wifi: {
      title: 'Verbinden met Wi-Fi',
      tutorial1: '1. Ga naar het aanraakscherm > Tik op "Netwerk" > "Verbind met WiFi".',
      tutorial1_ador: '1. Ga naar het aanraakscherm > Tik op "MACHINE" > Tik op "Netwerk" > "Verbind met WiFi".',
      tutorial2: '2. Selecteer en verbind met uw voorkeurs Wi-Fi.',
      what_if_1: 'Wat als ik mijn Wi-Fi niet zie?',
      what_if_1_content: '1. De Wi-Fi-encryptiemodus moet WPA2 zijn of geen wachtwoord hebben.\n2. De encryptiemodus kan worden ingesteld in de Wi-Fi-routerbeheerinterface. Als de router WPA2 niet ondersteunt en u hulp nodig heeft bij het uitkiezen van de juiste router, neem dan contact op met FLUX-ondersteuning.\n3. Het Wi-Fi-kanaal moet 2.4Ghz zijn (5Ghz wordt niet ondersteund).',
      what_if_2: 'Wat als ik helemaal geen Wi-Fi zie?',
      what_if_2_content: '1. Zorg ervoor dat de Wi-Fi-dongle volledig is aangesloten.\n2. Als er geen MAC-adres van het draadloze netwerk op het aanraakscherm staat, neem dan contact op met FLUX-ondersteuning.',
    },
    connect_wired: {
      title: 'Verbinden met bekabeld netwerk',
      tutorial1: '1. Verbind de machine met uw router.',
      tutorial2: '2. Druk op "Netwerk" om het IP-adres van het bekabelde netwerk te krijgen.',
      tutorial2_ador: '2. Druk op "MACHINE" > "Netwerk" om het IP-adres van het bekabelde netwerk te krijgen.',
      what_if_1: 'Wat als het IP-adres leeg is?',
      what_if_1_content: '1. Zorg ervoor dat de ethernetkabel volledig is aangesloten.\n2. Als er geen MAC-adres van het bekabelde netwerk op het aanraakscherm staat, neem dan contact op met FLUX-ondersteuning.',
      what_if_2: 'Wat als het IP begint met 169?',
      what_if_2_content: '1. Als het IP-adres begint met 169.254, zou het een DHCP-instellingsprobleem moeten zijn, neem voor verdere hulp contact op met uw ISP (internet service provider).\n2. Als uw computer direct verbinding maakt met internet via PPPoE, verander dit dan naar verbinding maken via de router met PPPoE en schakel de DHCP-functie op de router in.',
    },
    connecting: 'Verbinden...',
    connection_types: {
      ether2ether: 'Directe verbinding',
      usb: 'USB-verbinding',
      wifi: 'Wi-Fi',
      wired: 'Bekabeld netwerk',
    },
    next: 'Volgende',
    no_machine: 'Ik heb nu geen machine.',
    promark: {
      configuration_confirmation: 'Dit zorgt ervoor dat uw Promark correct is geconfigureerd voor optimale efficiëntie en nauwkeurigheid.',
      or_complete_later: `Of sla deze stap over en voltooi de Promark-instellingen later bij:
    Machines > "Promark Naam" > Promark-instellingen`,
      qc_instructions: 'Vul de parameters in die op de achterkant van de "QC Pass"-kaart staan',
      select_laser_source: 'Selecteer uw Promark',
      select_workarea: 'Selecteer Werkgebied',
      setting_completed_ask_camera_calibration: 'De Promark-veldlensinstallatie is succesvol voltooid! Wilt u doorgaan met de camerakalibratie?',
      settings: 'Promark-instellingen',
    },
    retry: 'Opnieuw',
    select_beambox: 'Selecteer uw Beambox',
    select_beamo: 'Selecteer uw beamo',
    select_connection_type: 'Hoe wilt u verbinden?',
    select_hexa: 'Selecteer uw HEXA',
    select_language: 'Selecteer Taal',
    select_machine_type: 'Selecteer Uw Machinetype',
    select_promark: 'Selecteer uw Promark',
    setting_completed: {
      back: 'Terug',
      great: 'Welkom bij Beam Studio',
      ok: 'BEGIN MET ONTWERPEN',
      setup_later: 'U kunt uw machine altijd instellen via de titelbalk > "Machines" > "Machine instellen"',
      start: 'Start',
    },
    skip: 'Overslaan',
    start: 'Starten',
  },
  input_machine_password: {
    connect: 'VERBINDEN',
    password: 'Wachtwoord',
    require_password: '"%s" vereist een wachtwoord',
  },
  insecure_websocket: {
    extension_detected: 'Beam Studio Connect-extensie gedetecteerd',
    extension_detected_description: "We hebben gedetecteerd dat u de Beam Studio Connect-extensie heeft geïnstalleerd. Klik op 'Bevestigen' om door te verwijzen naar HTTPS, of klik op 'Annuleren' om HTTP te blijven gebruiken.",
    extension_not_deteced: 'Kan Beam Studio Connect-extensie niet detecteren',
    extension_not_deteced_description: "Om HTTPS te gebruiken, klik op 'Bevestigen' om de Beam Studio Connect-extensie te installeren. Nadat u de extensie hebt geïnstalleerd, ververs de pagina om deze te activeren.<br/>Anders klikt u op de onderstaande link om te zien hoe u HTTP op Chrome kunt gebruiken.",
    unsecure_url_help_center_link: '<a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/9935859456271">Link</a>',
  },
  keychain_generator: {
    decoration: 'Decoratie',
    design_preview: 'Ontwerpvoorbeeld',
    element: 'Element',
    element_size: 'Grootte',
    emboss: 'Reliëf',
    exploded_view: 'Exploded weergave',
    export_failed: 'Kan sleutelhanger niet importeren naar het canvas. Probeer het opnieuw.',
    hole: 'Gat',
    hole_options: {
      diameter: 'Diameter',
      hole_type: 'Type',
      offset: 'Offset',
      position: 'Positie',
      thickness: 'Dikte',
      type_punch: 'Ponsgat',
      type_ring: 'Ring',
    },
    import_to_canvas: 'Importeren naar canvas',
    layers: {
      keychain: 'Sleutelhanger - Omtrek',
      keychain_deco_emboss: 'Sleutelhangerversieringen - Snijden',
      keychain_deco_emboss_guide: 'Sleutelhangerversieringen - Snijgids',
      keychain_deco_engraving: 'Sleutelhangerversieringen - Graveren',
    },
    more_shapes: 'Meer vormen',
    outline_offset: 'Omtrek offset',
    reset_to_default: 'Standaardwaarden herstellen',
    shape: 'Vorm',
    size_scale: 'Grootteschaal',
    switch_type_warning: 'Uw huidige instellingen zijn nog niet geïmporteerd naar het canvas.\nHet wisselen van sleutelhangertype zal alle instellingen terugzetten naar de standaardwaarden.',
    text: 'Tekst',
    text_options: {
      font_family: 'Lettertype-familie',
      font_settings: 'Lettertype-instellingen',
      font_size: 'Lettergrootte',
      font_style: 'Letterstijl',
      letter_spacing: 'Letterafstand',
      line_spacing: 'Regelafstand',
      text_placeholder: 'Tekst invoeren...',
    },
    types: {
      animal: 'Dierenstijl',
      capsule: 'Capsulestijl',
      dialog_box: 'Dialoogvensterstijl',
      geometry: 'Geometrische stijl',
      oval: 'Ovale stijl',
      pet: 'Huisdierstijl',
      plant: 'Plantstijl',
      polygonal: 'Veelhoekige stijl',
      quadrilateral: 'Vierhoekige stijl',
      round_arch: 'Ronde boogstijl',
      rounded: 'Ronde stijl',
      surfing_board: 'Surfplankstijl',
      tag: 'Labelstijl',
      text: 'Tekst',
      zodiac: 'Sterrenbeeld',
    },
  },
  layer_module: {
    general_laser: 'Laser',
    laser_2w_infrared: '2W Infraroodlaser',
    laser_10w_diode: '10W Diode Laser',
    laser_20w_diode: '20W Diode Laser',
    non_working_area: 'Niet-Werkgebied',
    none: 'None',
    notification: {
      convertFromLaserModuleMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de instellingen van de laserlaag worden verwijderd en ingesteld volgens de huidige laag.',
      convertFromLaserModuleTitle: 'Wilt u de lasermodule converteren naar de printmodule?',
      convertFromPrintingModuleMsg: 'Houd er rekening mee dat als u deze bewerking voltooit, de kleurinstellingen van de printlaag worden verwijderd en ingesteld volgens de huidige laag.',
      convertFromPrintingModuleTitle: 'Wilt u de Printmodule converteren naar de Lasermodule?',
      importedDocumentContainsPrinting: 'Het document bevat een printlaag, wilt u het werkgebied wijzigen naar printen?',
      importedDocumentContainsPrinting4C: 'Het document bevat een printlaag, wilt u het werkgebied wijzigen naar beamo II?',
      performIRCaliMsg: 'Klik op "Bevestigen" om de kalibratie uit te voeren, of open de kalibratie via het bovenste menu. <br /> (Machine> [Uw machinenaam]> Kalibreer lasermodule)',
      performIRCaliTitle: 'Voer de kalibratie van de lasermodule uit',
      performPrintingCaliMsg: 'Klik op "Bevestigen" om de kalibratie uit te voeren, of open de kalibratie via het bovenste menu. <br /> (Machine> [Uw machinenaam]> Kalibreer printmodule)',
      performPrintingCaliTitle: 'Voer de kalibratie van de printmodule uit',
      printingLayersConverted: 'De printlagen zijn geconverteerd naar laserlagen.',
    },
    printing: 'Printen',
    unknown: 'Unknown Module',
    uv_print: 'UV-print',
    uv_varnish: 'Lak',
    uv_white_ink: 'Witte inkt',
  },
  machine_linking: {
    continue: 'Doorgaan',
    i_have_read: 'Ik heb gelezen',
    input_birthday_to_link: 'Voer uw geboortedatum in om de apparaatkoppeling te voltooien',
    input_birthday_to_link_note: '*Wordt gebruikt om te verifiëren of uw leeftijd voldoet aan de geschiktheidsvereisten',
    link_failed: 'Koppelen mislukt.',
    link_failed_already_linked: 'Dit apparaat is momenteel gekoppeld aan een andere FLUX ID. U kunt het blijven gebruiken of aan uw account koppelen.',
    link_failed_cannot_set_secret: 'Kan koppelingsinformatie niet op het apparaat opslaan. Controleer de verbinding tussen uw computer en het apparaat.',
    link_failed_under_age: 'U voldoet momenteel niet aan de vereisten om deze dienst te gebruiken en kunt de apparaatkoppeling niet voltooien.',
    link_machine: 'FLUX ID koppelen',
    link_machine_to_account: 'Verbonden met %(name)s. Wilt u dit apparaat aan uw account koppelen?',
    link_success: 'Koppelen gelukt.',
    link_to_my_flux_id: 'Koppelen aan mijn FLUX ID',
    privacy_policy: 'Privacybeleid',
    privacy_policy_link: 'https://flux3dp.com/privacy/',
    process_linking: 'Koppelen…',
    use_without_linking: 'Gebruiken zonder koppelen',
  },
  machine_status: {
    '-17': 'Cartridge IO-modus',
    '-10': 'Onderhoudsmodus',
    '-2': 'Scannend',
    '-1': 'Onderhouden',
    '0': 'Inactief',
    '1': 'Initialiseren',
    '2': 'ST_TRANSFORM',
    '4': 'Opstarten',
    '6': 'Hervatten',
    '16': 'Werkend',
    '18': 'Hervatten',
    '32': 'Gepauzeerd',
    '36': 'Gepauzeerd',
    '38': 'Pauzerend',
    '48': 'Gepauzeerd',
    '50': 'Pauzerend',
    '64': 'Voltooid',
    '66': 'Voltooiend',
    '68': 'Voorbereidend',
    '128': 'Afgebroken',
    '256': 'Alarm',
    '512': 'Fataal',
    UNKNOWN: 'Onbekend',
  },
  maintenance: {
    actions: {
      checked: 'Gecontroleerd',
      done: 'Voltooid',
      fail: 'Mislukt',
      history: 'Geschiedenis',
      open_guide: 'Handleiding openen',
      pass: 'Geslaagd',
      print: 'Printen / PDF',
    },
    areas: {
      back_cover: 'Achterklep',
      modules: 'Modules',
      optics: 'Optiek',
      panel: 'Paneel',
      working_area: 'Werkgebied',
    },
    cadence: {
      by_material: 'Per materiaal',
      condition: 'Voorwaarde',
      day: 'Elke dag',
      days: 'Elke %s dagen',
      each_operation: 'Elk gebruik',
      every_1_2_weeks: 'Elke 1–2 weken',
      month: 'Elke maand',
      months: 'Elke %s maanden',
      upon_unboxing: 'Bij het eerste uitpakken',
      week: 'Elke week',
      weeks: 'Elke %s weken',
    },
    celebration: {
      subtitle: 'Uw %s is in topconditie — ga zo door.',
      title: 'Al het essentiële onderhoud is voltooid!',
    },
    columns: {
      cadence: 'Frequentie',
      status: 'Status',
      task: 'Taak',
    },
    empty_no_machine: 'Verbind of selecteer een machine om het onderhoudsschema te zien.',
    empty_no_schedule: 'Voor dit model is nog geen onderhoudschecklist beschikbaar.',
    essential: 'Essentieel',
    essential_health: 'Essentiële conditie',
    essential_hint: 'telt mee voor de conditie',
    feedback: {
      checked: '%s gecontroleerd.',
      done: '%s is bijgewerkt. Volgende beurt %s.',
      done_no_date: '%s is voltooid.',
      fail_warning: '%s: gemarkeerd voor vervanging van de laserbuis. Open de handleiding voor de stappen.',
      passed: '%s geslaagd — de laserbuis is gezond!',
    },
    footer_tip: 'Tip: graveer maandelijks een voorbeeldbestand om de machine gezond te houden.',
    history_hint: 'Alleen de %s meest recente vermeldingen worden per taak bewaard.',
    intro: 'Volg elke onderhoudstaak voor deze machine en markeer deze met één klik als voltooid.',
    legend: {
      due_soon: 'Binnenkort',
      overdue: 'Verlopen',
      up_to_date: 'Up-to-date',
    },
    machine_groups: {
      my_machines: 'Mijn machines',
      other_models: 'Andere modellen',
    },
    machine_label: 'Machine',
    materials: {
      acrylic: 'Acryl',
      leather: 'Leer',
      paper: 'Papier',
      wood: 'Hout',
    },
    status: {
      checked: 'Gecontroleerd',
      due: 'Te doen',
      healthy: 'Gezond',
      last: 'Laatst gedaan',
      never_done: 'Nooit gedaan',
      next: 'Volgende beurt',
      not_checked: 'Niet gecontroleerd',
      not_logged: 'Niet geregistreerd',
      overdue: 'Verlopen',
      replace_tube: 'Laserbuis vervangen',
    },
    tasks: {
      align: {
        keyPoints: 'Stel bij wanneer het optische pad niet is uitgelijnd.',
        name: 'Uitlijning optisch pad',
      },
      chassis: {
        keyPoints: 'Verwijder resten en teerolie van het chassis en de onderklep.',
        name: 'Chassis reinigen',
      },
      door: {
        keyPoints: 'Verwijder vet van de deurklep.',
        name: 'Deurklep reinigen',
      },
      exhaust: {
        keyPoints: 'Verwijder het stof van de ventilatorbladen.',
        name: 'Afzuigventilator reinigen',
      },
      fan: {
        keyPoints: 'Verwijder stof van de bladen van de ventilatieventilator.',
        name: 'Ventilatieventilator reinigen',
      },
      fieldlens: {
        keyPoints: 'Reinig de veldlens en veeg vanuit het midden naar buiten met de klok mee.',
        name: 'Veldlens reinigen',
      },
      honeycomb_plate: {
        keyPoints: 'Verwijder resten en teer uit de roosters van de honingraatplaat.',
        name: 'Honingraatplaat reinigen',
      },
      honeycomb_table: {
        keyPoints: 'Verwijder resten en teerolie van het honingraatgaas.',
        name: 'Honingraattafel reinigen',
      },
      lube: {
        keyPoints: 'Reinig de geleidestangen, de lineaire rail en het focusmechanisme en smeer ze opnieuw. De frequentie varieert met het materiaal dat u snijdt.',
        name: 'Smeren',
      },
      maintain_test: {
        keyPoints: 'Controleer het statusscherm en de schakelaars.',
        name: 'MAINTAIN-pagina',
      },
      mirrors_combiner: {
        keyPoints: 'Verwijder vuil van lenzen/spiegels; controleer op oxidatie. Laat geen alcohol- of watervlekken achter.',
        name: 'Spiegels, focuslens en beam combiner',
      },
      mirrors_lens: {
        keyPoints: 'Reinig de 4 spiegels en de lens; zorg dat er geen oxidatie of watervlekken zijn.',
        name: 'Spiegels en lens reinigen',
      },
      mod_diode: {
        keyPoints: 'Reinig de ventilator, de focuslens en de POGO pin.',
        name: 'Diode lasermodule',
      },
      mod_ir: {
        keyPoints: 'Reinig de focuslens.',
        name: 'Infrarood lasermodule',
      },
      optical_inspection: {
        keyPoints: 'Inspecteer het optische pad; ook bij het uitpakken en na vervanging van de laserbuis.',
        name: 'Inspectie optisch pad',
      },
      optical_unboxing: {
        keyPoints: 'Zorg dat het optische pad nauwkeurig is. Controleer bij het eerste uitpakken.',
        name: 'Inspectie optisch pad',
      },
      screen: {
        keyPoints: 'Verwijder het stof en vet op het paneel.',
        name: 'Scherm reinigen',
      },
      tube: {
        keyPoints: 'Als 3 mm acryl niet kan worden gesneden bij 70% vermogen, vervang dan de laserbuis.',
        name: 'Conditiecontrole laserbuis',
      },
      water: {
        keyPoints: 'Vul water bij tot meer dan 80% vol, zonder luchtbellen.',
        name: 'Koelwater vervangen',
      },
      worktable: {
        keyPoints: 'Spuit alcohol op een doekje en veeg daarmee de werktafel schoon.',
        name: 'Werktafel reinigen',
      },
      zaxis: {
        keyPoints: 'Veeg de schroefspindel van de Z-askolom af en breng gelijkmatig smeermiddel aan met een wattenstaafje.',
        name: 'Smering Z-askolom',
      },
    },
    title: 'Onderhoudschecklist',
  },
  material_test_generator: {
    block_settings: 'Blokinstellingen',
    columns: 'Kolommen',
    count: 'Aantal',
    cut: 'Knippen',
    engrave: 'Graveren',
    export: 'Exporteren',
    max: 'Max',
    min: 'Min',
    parameter: 'Parameter',
    rows: 'Rijen',
    size: 'Grootte (HxB)',
    spacing: 'Afstand',
    table_settings: 'Tabelinstellingen',
    text_settings: 'Tekstinstellingen',
    title: 'Materiaaltestgenerator',
  },
  menu: {
    inches: 'Duimen',
    mm: 'mm',
  },
  message: {
    auth_error: '#820 Auth-fout: Werk Beam Studio en apparaatfirmware bij naar de nieuwste versie.',
    authenticating: 'Authenticeren...',
    auto_focus: {
      instruction: 'Verplaats de lasermodulekop boven het object en volg de animatie-instructies om op AF te drukken om scherp te stellen.',
      succeeded: 'Succesvol scherpgesteld',
      title: 'Auto scherpstellen',
      warning_at_coordinates: 'Na het activeren van de automatische focusmodus wordt de laserkop gereset en naar de opgegeven positie bewogen om scherp te stellen.\nZorg ervoor dat de doelpositie op het materiaal ligt en dat er geen obstakels in het bewegingspad zijn.',
    },
    backend_calculation: {
      analyzing_svg: 'SVG analyseren - %d%% ...',
      calculating_task_path: 'Taakpad berekenen - %d%% ...',
      finishing: 'Afronden...',
      initializing: 'Initialiseren...',
    },
    camera: {
      abort_preview: 'Afbreken',
      calibration_data_missing: 'Camerakalibratie instellen',
      calibration_data_missing_message: 'Er zijn geen camerakalibratiegegevens.\nImporteer uw opgeslagen kalibratiegegevens of voer de camerakalibratie uit.',
      calibration_wide_angle_camera_first: 'Kalibreer eerst de groothoeklens camera.',
      camera_cable_unstable: "Er is gedetecteerd dat de camera foto's instabiel overdraagt. Voorbeeldweergave kan nog steeds normaal worden uitgevoerd, maar er kan sprake zijn van trage voorbeeldweergave of time-out.",
      continue_preview: 'Doorgaan',
      door_opened: 'Deur geopend',
      door_opened_text: 'Sluit de deur voordat u de cameravoorvertoning opnieuw start.',
      fail_to_transmit_image: '#845 Er ging iets mis met beeldoverdracht. Start uw machine of Beam Studio opnieuw op. Als deze fout aanhoudt, volg dan <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/4402756056079">deze handleiding</a>.',
      switching_camera: 'Camera wisselen...',
      wide_angle_open_door: 'Open het deksel',
      wide_angle_open_door_text: 'Open het deksel voordat u overschakelt naar de groothoekcamera.',
      ws_closed_unexpectedly: '#844 Verbinding met machinecamera onverwacht verbroken. Als deze fout aanhoudt, volg dan <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/4402755805071">deze handleiding</a>.',
    },
    cancelling: 'Annuleren...',
    cant_connect_to_device: '#827 Kan geen verbinding maken met de machine, controleer uw verbinding',
    connected: 'Verbonden',
    connecting: 'Verbinden...',
    connectingCamera: 'Camera verbinden...',
    connectingMachine: 'Verbinden met %s...',
    connectionTimeout: '#805 Apparaat verbinding time-out. Controleer uw netwerkstatus en de Wi-Fi-indicator van uw machine.',
    device_blocked: {
      caption: 'Niet-geautoriseerd serienummer',
      offline: 'Het serienummer van uw apparaat is beperkt tot offline gebruik omdat het gedeactiveerd lijkt te zijn. Geef het serienummer van het apparaat (te vinden aan de achterkant van het apparaat) aan uw verkoper en vraag hem contact op te nemen met support@flux3dp.com om de online autorisatie te activeren. Als u het apparaat offline wilt gebruiken, neem dan rechtstreeks contact op met support@flux3dp.com.',
      online: 'Het serienummer van uw apparaat lijkt gedeactiveerd te zijn. Geef het serienummer van het apparaat (te vinden aan de achterkant van het apparaat) aan uw verkoper en vraag hem contact op te nemen met support@flux3dp.com om de apparaatautorisatie te activeren.',
    },
    device_busy: {
      caption: 'Apparaat bezet',
      message: 'Het apparaat voert een andere taak uit, probeer het later opnieuw. Als het apparaat niet meer werkt, start het dan opnieuw op.',
    },
    device_is_used: 'Het apparaat wordt gebruikt, wilt u de huidige taak annuleren?',
    device_not_found: {
      caption: 'Standaardmachine niet gevonden',
      message: '#812 Controleer de Wi-Fi-indicator van uw apparaat',
    },
    disconnected: 'Verbinding instabiel, controleer apparaatverbinding en probeer het later opnieuw',
    endingLineCheckMode: 'Lijncontrolemodus sluiten...',
    endingRawMode: 'Machinebesturingsmodus afsluiten...',
    enteringLineCheckMode: 'Lijncontrolemodus openen...',
    enteringRawMode: 'Machinebesturingsmodus starten...',
    enteringRedLaserMeasureMode: 'Rood-laser meetmodus wordt ingeschakeld...',
    exitingRotaryMode: 'Rotatiemodus sluiten...',
    getProbePosition: 'Probe-positie ophalen...',
    gettingLaserSpeed: 'Lasersnelheid lezen...',
    homing: 'Refereren...',
    mac_os_arch_mismatch: {
      caption: 'Compatibele versie opnieuw installeren',
      download_center: 'Downloadcentrum',
      message: 'Deze Mac gebruikt Apple Silicon. Download de Apple Silicon versie en installeer opnieuw.',
    },
    need_password: 'Wachtwoord vereist om verbinding te maken met het apparaat',
    please_enter_dpi: 'Voer de eenheid van uw bestand in (in mm)',
    preview: {
      adjust: 'Aanpassen',
      adjust_height_tooltip: 'Schakel het selectievakje in om bewerken mogelijk te maken.',
      already_performed_auto_focus: 'U heeft al automatisch scherpgesteld, bestaande waarden gebruiken?',
      camera_preview: 'Camera voorbeeldweergave',
      capturing_image: 'Afbeelding vastleggen...',
      enter_manually: 'Handmatig invoeren',
      getting_focal_distance: 'Bezig met ophalen van brandpuntsafstand...',
      moving_to_preview_position: 'Bezig met verplaatsen naar voorbeeldpositie...',
      please_enter_height: 'Voer de hoogte van het object in om een nauwkeurige camerafoto te maken.',
      please_focus_before_preview: 'Voor een betere voorbeeldkwaliteit, voer automatisch scherpstellen uit voordat u een voorbeeld bekijkt.',
      press_esc_to_stop: 'Druk op ESC om de cameravoorvertoning te stoppen.',
      succeeded: 'Succesvol voorvertoond',
    },
    promark_disconnected: '#850 Apparaatverbinding onderbroken, controleer de verbindingsstatus van het apparaat.',
    redLaserTakingReference: 'Referentie wordt genomen...',
    retrievingCameraOffset: 'Camera-offset lezen...',
    settingLaserSpeed: 'Lasersnelheid instellen...',
    swiftray_disconnected: 'Kan geen verbinding maken met de backend, opnieuw verbinden.',
    swiftray_reconnected: 'Backend opnieuw verbonden, probeer de taak opnieuw te verzenden.',
    time_remaining: 'Resterende tijd:',
    tryingToConenctMachine: 'Proberen te verbinden met machine...',
    turningOffAirPump: 'Luchtpomp uitschakelen...',
    turningOffFan: 'Ventilator uitschakelen...',
    unable_to_find_machine: 'Kan machine niet vinden',
    unable_to_start: '#830 Kan de taak niet starten. Probeer het opnieuw. Als dit opnieuw gebeurt, neem dan contact met ons op met een bugrapportage:',
    unavailableWorkarea: '#804 Huidige werkgebied overschrijdt het werkgebied van dit apparaat. Controleer het werkgebied van het geselecteerde apparaat of stel het werkgebied in via Bewerken > Documentinstellingen.',
    unknown_device: '#826 Kan geen verbinding maken met het apparaat, zorg ervoor dat de USB is aangesloten op het apparaat',
    unknown_error: '#821 De applicatie heeft een onbekende fout gevonden, gebruik Help > Menu > Fout rapporteren.',
    unsupport_osx_version: 'De huidige macOS-versie %s ondersteunt mogelijk niet alle functies. Werk alsjeblieft bij naar macOS 13+.',
    unsupport_win_version: 'De huidige besturingssysteemversie %s ondersteunt mogelijk niet alle functies. Werk alsjeblieft bij naar de nieuwste versie.',
    unsupported_example_file: 'Het geselecteerde voorbeeldbestand wordt niet ondersteund door het huidige werkgebied.',
    uploading_fcode: 'FCode uploaden',
    usb_unplugged: 'USB-verbinding verbroken. Controleer uw USB-verbinding',
    wrong_swiftray_version_message: 'Onjuiste backend-versie (versie: {version}). Controleer of er meerdere instanties van Beam Studio actief zijn en sluit overtollige instanties voordat u het opnieuw probeert.',
    wrong_swiftray_version_title: 'Onjuiste backend-versie',
  },
  monitor: {
    ask_reconnect: 'De verbinding met de machine is verbroken. Wilt u opnieuw verbinden?',
    bug_report: 'Foutrapport',
    camera: 'Camera',
    cancel: 'Annuleren',
    confirmFileDelete: 'Weet u zeker dat u dit bestand wilt verwijderen?',
    connecting: 'Verbinden...',
    DEVICE_ERROR: 'Er ging iets mis. Start de machine opnieuw op.',
    download: 'Downloaden',
    extensionNotSupported: 'Dit bestandsformaat wordt niet ondersteund',
    fileExistContinue: 'Het bestand bestaat al, wilt u het overschrijven?',
    forceStop: 'Wilt u de huidige taak afbreken?',
    go: 'Start',
    HARDWARE_ERROR_AIR_ASSIST_ABNORMAL: '#913 Luchtstroomsensor afwijkend',
    HARDWARE_ERROR_BOTTOM_OPENED: '#903 Onderkant geopend. Sluit de onderkant om door te gaan.',
    HARDWARE_ERROR_CARTRIDGE_OVERHEAT: '#931 Temperatuurwaarschuwing inktcartridge\nDe temperatuur van de inktcartridge ligt boven het normale bereik.\nTer bescherming is het werk onderbroken. Installeer de inktcartridge opnieuw en herstart het werk.',
    HARDWARE_ERROR_CARTRIDGE_POWERON_FAILED: '#930 Installeer de inktcartridge opnieuw\nSlechte verbinding van de inktcartridge gedetecteerd. Installeer de inktcartridge opnieuw en probeer het opnieuw.',
    HARDWARE_ERROR_COVER_NOT_CLOSED: '#924 Installeren printkopkap mislukt',
    HARDWARE_ERROR_COVER_NOT_OPENED: '#925 Verwijderen printkopkap mislukt',
    HARDWARE_ERROR_DOOR_OPENED: '#901 Sluit de deur om door te gaan.',
    HARDWARE_ERROR_DRAWER_OPENED: '#911 Lade geopend',
    HARDWARE_ERROR_FIRE_DETECTED: '#912 Vlamsensor afwijkend',
    HARDWARE_ERROR_HEADTYPE_MISMATCH: '#915 Onjuiste module gedetecteerd. Installeer de juiste module om door te gaan.',
    HARDWARE_ERROR_HEADTYPE_NONE: '#917 Module niet gedetecteerd. Zorg dat de module juist is geïnstalleerd om door te gaan.',
    HARDWARE_ERROR_HEADTYPE_UNKNOWN: '#918 Onbekende module gedetecteerd. Installeer de juiste module om door te gaan.',
    HARDWARE_ERROR_HOMING_PULLOFF_FAILED: '#920 Homing pull-off fout',
    HARDWARE_ERROR_KEY_OFF: '#929 Veiligheidssleutel niet gedetecteerd\nDe veiligheidssleutel wordt niet gedetecteerd of is gedeactiveerd. Voor uw veiligheid kan de machine niet werken totdat de sleutel correct is ingeschakeld.\nPlaats de veiligheidssleutel en schakel deze in voordat u het werk herstart.',
    HARDWARE_ERROR_LASER_TEMPERATURE_ABNORMAL: '#933 Abnormale lasertemperatuur\nDe lasertemperatuur is te hoog. Stop de bewerking en laat de laser afkoelen voordat u herstart.',
    HARDWARE_ERROR_LASER_VOLTAGE_ABNORMAL: '#932 Abnormale laserspanning\nLaserspanningsfout. Herstart de machine. Als het probleem aanhoudt, neem contact op met de klantenservice.',
    HARDWARE_ERROR_MAINBOARD_ERROR: '#401 Kritieke fout: Hoofdprintplaat offline. Neem contact op met FLUX-ondersteuning.',
    HARDWARE_ERROR_MAINBOARD_NO_RESPONSE: '#928 Hoofdprintplaat reageert niet\nDe hoofdprintplaat reageert niet. Herstart de taak.',
    HARDWARE_ERROR_OVER_TEMPERATURE: '#902 Oververhit. Wacht een paar minuten.',
    HARDWARE_ERROR_PLATFORM_HOMING_FAILED: '#910 Z-as reset mislukt',
    HARDWARE_ERROR_PRINTER_NO_RESPONSE: 'Geen reactie van printermodule.',
    HARDWARE_ERROR_PROBE_SHOWED: 'Trek de probe alstublieft in.',
    HARDWARE_ERROR_PUMP_ERROR: '#900 Lage koelvloeistofstroom. Controleer uw watertank.',
    HARDWARE_ERROR_REMOTE_INTERLOCK_OPENED: '#927 Externe vergrendeling geactiveerd\nControleer de connector van de externe vergrendeling en zorg ervoor dat deze goed is aangesloten voordat u het werk herstart.',
    HARDWARE_ERROR_ROTARY_NOT_DETECTED: '#914 Geen draaitafelmodule gedetecteerd',
    HARDWARE_ERROR_SUBSYSTEM_ERROR: '#402 Kritieke fout: Geen reactie van subsysteem. Neem contact op met FLUX-ondersteuning.',
    HARDWARE_FAILURE: 'Er ging iets mis. Start de machine opnieuw op.',
    hour: 'u',
    left: 'links',
    MAINBOARD_OFFLINE: '#905 Fout bij verbinden met moederbord. Start de machine opnieuw op.',
    minute: 'm',
    monitor: 'MONITOR',
    NO_RESPONSE: '#905 Fout bij verbinden met moederbord. Start de machine opnieuw op.',
    no_task_info: 'Er zijn momenteel geen taken om weer te geven.',
    pause: 'Pauzeren',
    prepareRelocate: 'Voorbereiden op verplaatsen',
    processing: 'Verwerken',
    record: 'Opnemen',
    relocate: 'Verplaatsen',
    RESOURCE_BUSY: 'De machine is bezet. Herstart de machine als deze niet draait.',
    resume: 'Hervatten',
    savingPreview: 'Miniatuurweergaven genereren',
    second: 's',
    start: 'Start',
    stop: 'Stoppen',
    SUBSYSTEM_ERROR: '#402 Kritieke fout: subsysteem reageert niet. Neem contact op met FLUX-ondersteuning.',
    task: {
      BEAMBOX: 'Laser graveren',
      'N/A': 'Vrije modus',
    },
    taskTab: 'Taak',
    temperature: 'Temperatuur',
    upload: 'Uploaden',
    USER_OPERATION: 'Volg de instructies op het apparaatpaneel om door te gaan.',
    USER_OPERATION_CARTRIDGE_INK_EMPTY: 'Cartridge leeg\nDe cartridge is leeg. Vervang de cartridge om verder te printen.',
    USER_OPERATION_CARTRIDGE_INK_LOW: 'Laag inktniveau\nDe inkt in de cartridge raakt bijna op. Vervang de cartridge binnenkort.',
    USER_OPERATION_CHANGE_CARTRIDGE: 'Plaats de juiste cartridge om door te gaan.',
    USER_OPERATION_CHANGE_CARTRIDGE_CARTRIDGE_NOT_DETECTED: 'Plaats de cartridge om door te gaan.',
    USER_OPERATION_CHANGE_TOOLHEAD: 'Installeer de juiste module om door te gaan.',
    USER_OPERATION_CHANGE_TOOLHEAD_HEADTYPE_MISMATCH: '#915 Onjuiste module gedetecteerd. Installeer de juiste module om door te gaan.',
    USER_OPERATION_CHANGE_TOOLHEAD_HEADTYPE_NONE: '#917 Module niet gedetecteerd. Zorg dat de module juist is geïnstalleerd om door te gaan.',
    USER_OPERATION_CHANGE_TOOLHEAD_HEADTYPE_UNKNOWN: '#918 Onbekende module gedetecteerd. Installeer de juiste module om door te gaan.',
    USER_OPERATION_REMOVE_CARTRIDGE: 'Verwijder de cartridge om door te gaan.',
    USER_OPERATION_ROTARY_PAUSE: 'Schakel over naar de draaimotor.',
    USER_OPERATION_USER_Z_AXIS_CHECK_REQUIRED: 'Focusherinnering\nStel vóór een nieuwe opdracht opnieuw scherp voor nauwkeurige resultaten.',
  },
  my_cloud: {
    action: {
      confirmFileDelete: 'Weet u zeker dat u dit bestand wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.',
      delete: 'Verwijderen',
      download: 'Downloaden',
      duplicate: 'Dupliceren',
      open: 'Openen',
      rename: 'Hernoemen',
    },
    file_limit: 'Gratis bestand',
    loading_file: 'Bezig met laden...',
    no_file_subtitle: 'Ga naar Menu > "Bestand" > "Opslaan in cloud"',
    no_file_title: 'Sla bestanden op in Mijn cloud om te beginnen.',
    not_login_title: 'Log in om uw bestanden in de cloud op te slaan.',
    save_file: {
      choose_action: 'Bestand opslaan:',
      input_file_name: 'Voer bestandsnaam in:',
      invalid_char: 'Ongeldige tekens:',
      save: 'Opslaan',
      save_new: 'Opslaan als nieuw bestand',
      storage_limit_exceeded: 'Uw cloudopslag heeft de bovengrens bereikt. Verwijder onnodige bestanden voordat u nieuwe opslaat.',
    },
    sort: {
      a_to_z: 'Naam: A - Z',
      most_recent: 'Meest recent',
      oldest: 'Oudste',
      z_to_a: 'Naam: Z - A',
    },
    title: 'Mijn cloud',
    upgrade: 'Upgrade',
  },
  noun_project_panel: {
    clear: 'Wissen',
    elements: 'Elementen',
    enjoy_shape_library: 'Geniet van de vormenbibliotheek.',
    export_svg_title: 'Kan geen SVG exporteren',
    export_svg_warning: 'Dit project bevat objecten die beschermd zijn door de intellectuele eigendomswet. Beam Studio zal deze objecten daarom automatisch uitsluiten tijdens de export. U kunt uw project nog steeds opslaan in het Beam Studio Scene (.beam)-formaat om al uw gegevens te behouden. Wilt u nog steeds exporteren?',
    learn_more: 'Meer informatie',
    login_first: 'Log in om de vormendatabase te ontgrendelen.',
    recent: 'Recent',
    search: 'Zoeken',
    shapes: 'Vormen',
  },
  pass_through: {
    export: 'Exporteren naar Werkgebied',
    exporting: 'Exporteren...',
    guide_mark: 'Gidsmarkering',
    guide_mark_desc: 'Gidsmarkeringen worden gegraveerd als referentiepunt om het kunstwerk uit te lijnen.',
    guide_mark_length: 'Lengte:',
    guide_mark_x: 'X-coördinaat:',
    height_desc: 'Stel de hoogte van elke werkgebiedsectie in.',
    help_links: {
      ado1: 'https://support.flux3dp.com/hc/en-us/articles/10140002160399',
      fbb2: 'https://support.flux3dp.com/hc/en-us/articles/11570021253263',
    },
    help_text: 'Hoe stel je doorgang in voor %(model)s?',
    object_length: 'Objectlengte',
    ref_layer: 'Referentielaag',
    ref_layer_desc: 'Let op dat de uitvoering van de referentielaag standaard is ingesteld op 0. Het wordt niet uitgevoerd, maar dient alleen als uitlijningsreferentie.',
    ref_layer_name: 'Referentie',
    title: 'Doorgangsmodus',
    workarea_height: 'Werkgebied (Hoogte):',
  },
  promark_connection_test: {
    description: 'Het systeem voert ongeveer twee minuten een roodlichtbewerking uit om te controleren of de verbinding stabiel is.',
    health: 'Stabiliteit:',
    healthy_description: 'De stabiliteit is goed, maar vermijd aanzienlijke beweging van de computer tijdens het graveren.',
    res_0: 'Uitstekend',
    res_1: 'Goed',
    res_2: 'Laag',
    res_3: 'Te Laag',
    res_4: 'Zeer Laag',
    restart: 'Test Opnieuw Starten',
    start: 'Test Starten',
    stop: 'Test Stoppen',
    title: 'Verbindingsstabiliteitstest',
    unhealthy_description: 'De stabiliteit is te laag. Het wordt aanbevolen om de netwerkadapter of stroomkabel te vervangen.',
  },
  promark_settings: {
    angle: 'Hoek',
    bulge: 'Bolling',
    field: 'Veld',
    galvo_configuration: 'Galvo-configuratie',
    mark: 'Markeren',
    mark_parameters: 'Markeerparameters',
    offsetX: 'Offset X',
    offsetY: 'Offset Y',
    red_dot: 'Rode stip',
    scale: 'Schaal',
    scaleX: 'Schaal X',
    scaleY: 'Schaal Y',
    skew: 'Scheefheid',
    switchXY: 'X/Y wisselen',
    title: 'Promark-instellingen',
    trapezoid: 'Trapezium',
    workarea_hint: 'U kunt het werkgebied wijzigen in "Documentinstellingen".',
    z_axis_adjustment: {
      section1: 'Pas de hoogte van de Z-as aan om de focus te verfijnen.',
      title: 'Z-As Aanpassing',
      tooltip1: 'Probeer een vierkant van 1x1 cm te markeren om te bevestigen of de huidige brandpuntsafstand geschikt is.',
    },
  },
  puzzle_generator: {
    bleed: 'Afloop',
    board_base: 'Bordbasis',
    board_radius: 'Randradius',
    board_width: 'Randbreedte',
    change_image: 'Klik om afbeelding te wijzigen',
    columns: 'Kolommen',
    design_preview: 'Ontwerpvoorbeeld',
    exploded_view: 'Explosieweergave',
    export_as: 'Exporteren als',
    export_engrave: 'Graveerlaag',
    export_failed: 'Kan puzzel niet naar canvas importeren. Probeer het opnieuw of pas de puzzelinstellingen aan.',
    export_none: 'Geen',
    export_print: 'Afdruklaag',
    file_too_large: 'Het geselecteerde bestand overschrijdt de maximaal toegestane grootte.',
    guide_lines: 'Hulplijnen',
    heart_sharpness: 'Scherpte van de punt',
    image: 'Afbeelding',
    image_load_failed: 'Kan de geselecteerde afbeelding niet laden. Probeer een ander bestand.',
    image_resolution_warning: 'De beeldresolutie overschrijdt %(resolution)spx. Dit kan de exportkwaliteit of prestaties beïnvloeden.',
    import_to_canvas: 'Importeren naar canvas',
    layers: {
      board_base: 'Bordbasis',
      guide_lines: 'Hulplijnen',
      image: 'Puzzelafbeelding',
      pieces: 'Puzzelstukjes',
      raised_edges: 'Verhoogde randen',
    },
    offset_x: 'X-verschuiving',
    offset_y: 'Y-verschuiving',
    orientation: 'Stukken oriëntatie',
    orientation_type_1: 'Type 1',
    orientation_type_2: 'Type 2',
    orientation_type_3: 'Type 3',
    orientation_type_4: 'Type 4',
    piece_size: 'Stukgrootte',
    preview_placeholder: 'Puzzelvoorbeeld',
    puzzle_board: 'Puzzelbord',
    puzzle_pieces: 'Puzzelstukken',
    radius: 'Radius',
    raised_edges: 'Verhoogde randen',
    reset_to_default: 'Terug naar standaard',
    rows: 'Rijen',
    switch_type_warning: 'Uw huidige instellingen zijn nog niet naar het canvas geïmporteerd.\nHet wijzigen van het puzzeltype zal alle instellingen herstellen naar de standaardwaarden.',
    tab_size: 'Lipgrootte',
    types: {
      circle_jigsaw: 'Ronde puzzel',
      heart_jigsaw: 'Hartvormige puzzel',
      hex_jigsaw: 'Hexagonale puzzel',
      rectangle_jigsaw: 'Rechthoekige puzzel',
    },
    unsupported_file_type: 'Niet-ondersteund bestandstype. Gebruik JPG, PNG of WebP.',
    upload_hint: 'JPG, PNG, WebP (max 10MB)',
    upload_image: 'Klik of sleep om te uploaden',
    zoom: 'Zoom',
  },
  qr_code_generator: {
    error_tolerance: 'Foutcorrectie',
    error_tolerance_link: 'https://support.flux3dp.com/hc/en-us/articles/9113705072143',
    invert: 'Achtergrondkleur omkeren',
    placeholder: 'Tekst invoeren',
    title: 'QR-codegenerator',
  },
  resolution: {
    title: 'Resolutie',
    values: {
      detailed: 'Ultra Fijn',
      high: 'Fijn',
      low: 'Concept',
      medium: 'Standaard',
      mixed: 'Gemengd',
      ultra: 'Ultra HD',
    },
  },
  rotary_settings: {
    circumference: 'Omtrek',
    extend_workarea: 'Werkgebied uitbreiden',
    mirror: 'Spiegel',
    object_diameter: 'Objectdiameter',
    overlap_size: 'Overlapgrootte',
    rotary_scale: 'Rotatieschaal',
    split_setting: 'Splitsinstelling',
    split_setting_url: 'https://support.flux3dp.com/hc/en-us/articles/12276094010767',
    split_size: 'Splitsingsgrootte',
    type: 'Type',
  },
  select_device: {
    auth_failure: '#811 Verificatie mislukt',
    select_device: 'Selecteer apparaat',
    unable_to_connect: '#810 Kan geen stabiele verbinding maken met machine',
  },
  settings: {
    anti_aliasing: 'Antialiasing',
    auto_connect: 'Selecteer automatisch de enige machine',
    auto_switch_tab: 'Automatisch schakelen tussen laag- en objectpaneel',
    autofocus_offset: 'Autofocus offset',
    autosave_enabled: 'Automatisch opslaan',
    autosave_interval: 'Elke',
    autosave_number: 'Aantal automatisch opslaan',
    autosave_path: 'Locatie voor automatisch opslaan',
    autosave_path_not_correct: 'Opgegeven pad niet gevonden.',
    blade_precut_position: 'Voorinsnijpositie',
    blade_precut_switch: 'Mes voorinsnijding',
    blade_radius: 'Messtraal',
    bottom_up: 'Van onder naar boven',
    calculation_optimization: 'Versnelling van padberekening',
    cancel: 'Annuleren',
    caption: 'Instellingen',
    check_updates: 'Automatisch controleren op updates',
    close: 'Sluiten',
    confirm_remove_default: 'De standaardmachine wordt verwijderd.',
    confirm_reset: 'Beam Studio resetten bevestigen?',
    continuous_drawing: 'Doorlopende tekening',
    crop_task_thumbnail: 'Inhoud bijgesneden miniatuur',
    crop_task_thumbnail_tooltip: 'Toon alleen de miniatuur van het gebied met inhoud, exclusief lege canvasgebieden.',
    custom_preview_height: 'Aangepaste voorbeeldhoogte',
    default_beambox_model: 'Standaard documentinstellingen',
    default_borderless_mode: 'Open onderkant standaard',
    default_enable_autofocus_module: 'Autofocus standaard',
    default_enable_diode_module: 'Diode laser standaard',
    default_font_family: 'Standaard lettertype',
    default_font_style: 'Standaard lettertypestijl',
    default_laser_module: 'Standaard lasermodule',
    default_machine: 'Standaardmachine',
    default_machine_button: 'Leeg',
    default_units: 'Standaard eenheden',
    diode_offset: 'Diode laser offset',
    diode_one_way_engraving: 'Diode laser eenrichtingsgravering',
    diode_two_way_warning: 'Bidirectioneel licht uitstralen is sneller en genereert waarschijnlijk enige onnauwkeurigheid in de lasergraveerpositie. Aanbevolen om eerst te testen.',
    disabled: 'Uitgeschakeld',
    done: 'Toepassen',
    enable_custom_backlash: 'Schakel aangepaste backlash compensatie in',
    enable_low_speed: 'Schakel lage snelheidsbeweging in',
    enable_uv_print_file: 'UV-afdrukbestand inschakelen',
    enabled: 'Ingeschakeld',
    engraving_direction: 'Richting',
    engraving_offset_tooltip: 'Als het graveerresultaat naar links of omhoog is verschoven, verlaag dan de X/Y-verschuiving; als het naar rechts of omlaag is verschoven, verhoog dan de X/Y-verschuiving.',
    fast_gradient: 'Snelheidsoptimalisatie',
    font_convert: 'Tekst naar pad Converter',
    font_substitute: 'Vervang onondersteunde tekens',
    grouped_objects: 'Gegroepeerde objecten',
    groups: {
      ador_modules: 'Ador-plug-ins',
      autosave: 'Automatisch opslaan',
      beamo2_modules: 'beamo 2 modules',
      camera: 'Camera',
      connection: 'Verbinding',
      editor: 'Editor',
      engraving: 'Graveren (scannen)',
      general: 'Algemeen',
      mask: 'Werkgebied bijsnijden',
      module_settings: 'Module-instellingen',
      module_type: 'Machine module',
      modules: 'Plug-ins',
      path: 'Vector (omtrekken)',
      performance: 'Prestaties',
      privacy: 'Privacy',
      text: 'Tekst',
      text_to_path: 'Tekst',
      update: 'Software-updates',
      workarea: 'Werkgebied',
    },
    guess_poke: 'Zoek naar machine IP-adres',
    guides: 'Hulplijnen',
    guides_origin: 'Hulplijnen oorsprong',
    help_center_urls: {
      anti_aliasing: 'https://support.flux3dp.com/hc/en-us/articles/360004408956',
      calculation_optimization: 'https://support.flux3dp.com/hc/en-us/articles/11146997425039',
      connection: 'https://support.flux3dp.com/hc/en-us/sections/360000302135',
      continuous_drawing: 'https://support.flux3dp.com/hc/en-us/articles/360004406496',
      default_borderless_mode: 'https://support.flux3dp.com/hc/en-us/articles/360001104076',
      default_enable_autofocus_module: 'https://support.flux3dp.com/hc/en-us/articles/360001574536',
      default_enable_diode_module: 'https://support.flux3dp.com/hc/en-us/articles/360001568035',
      fast_gradient: 'https://support.flux3dp.com/hc/en-us/articles/360004496235',
      font_convert: 'https://support.flux3dp.com/hc/en-us/articles/9132766761743',
      font_substitute: 'https://support.flux3dp.com/hc/en-us/articles/360004496575',
      image_downsampling: 'https://support.flux3dp.com/hc/en-us/articles/360004494995',
      loop_compensation: 'https://support.flux3dp.com/hc/en-us/articles/360004408856',
      mask: 'https://support.flux3dp.com/hc/en-us/articles/360004408876',
      segmented_engraving: 'https://support.flux3dp.com/hc/en-us/articles/12306366019215',
      simplify_clipper_path: 'https://support.flux3dp.com/hc/en-us/articles/360004407276',
      uv_print_export: 'https://support.flux3dp.com/hc/en-us/articles/12320887477135',
      vector_speed_constraint: 'https://support.flux3dp.com/hc/en-us/articles/360004496495',
    },
    high: 'Hoog',
    image_downsampling: 'Bitmap voorbeeldkwaliteit',
    ip: 'Machine IP-adres',
    keep_preview_result: 'Voorbeeldresultaat Behouden',
    language: 'Taal',
    loop_compensation: 'Luscompensatie',
    low: 'Laag',
    low_laser_for_preview: 'Laser voor Frame uitvoeren',
    mask: 'Werkgebied bijsnijden',
    medium: 'Gemiddeld',
    module_offset_2w_ir: '2W infrarood laser offset',
    module_offset_10w: '10W diode laser offset',
    module_offset_20w: '20W diode laser offset',
    module_offset_laser: 'Laser offset',
    module_offset_printer: 'Printer offset',
    none: 'Geen',
    normal: 'Normaal',
    notification_off: 'Uit',
    notification_on: 'Aan',
    notifications: 'Bureaubladmeldingen',
    off: 'Uit',
    on: 'Aan',
    preview_movement_speed: 'Voorbeeld bewegingssnelheid',
    printer_advanced_mode: 'Printer geavanceerde modus',
    printing_offset_tooltip: 'Als het afdrukresultaat naar links of omhoog is verschoven, verlaag dan de X/Y-verschuiving; als het naar rechts of omlaag is verschoven, verhoog dan de X/Y-verschuiving.',
    remove_default_machine_button: 'Verwijderen',
    reset: 'Beam Studio resetten',
    reset_now: 'Beam Studio resetten',
    segmented_engraving: 'Gesegmenteerd graveren',
    share_with_flux: 'Beam Studio-analyses delen',
    show_banners: 'Startpagina-banner weergeven',
    simplify_clipper_path: 'Ankerpunt optimalisatie',
    single_object: 'Enkel object',
    tabs: {
      device: 'Machine',
      general: 'Algemeen',
    },
    text_path_calc_optimization: 'Padberekeningsoptimalisatie',
    top_down: 'Van boven naar beneden',
    trace_output: 'Afbeelding vectorisatie uitvoer',
    update_beta: 'Bèta',
    update_latest: 'Laatste',
    use_real_boundary: 'Gebruik maximaal bereik',
    use_real_boundary_tooltip: 'Uitbreiden tot het werkelijke maximale werkgebied, niet beperkt door gehele rasterlijnen.',
    use_union_boundary: 'Overlappend werkgebied weergeven',
    use_union_boundary_tooltip: 'Toon overlappende werkgebieden van meerdere modules',
    vector_order_optimization: 'Vectorvolgorde-optimalisatie',
    vector_speed_constraint: 'Snelheidslimiet',
    wrong_ip_format: 'Ongeldig IP-formaat',
  },
  social_media: {
    facebook: 'Chat met FLUXers, stel vragen en leer tips!',
    instagram: 'Ontvang de nieuwste inspiratie, aanbiedingen en gratis items!',
    youtube: 'Bekijk Beam Studio-tutorials en knutselideeën.',
  },
  stamp_maker_panel: {
    bevel_radius: 'Afschuining radius',
    horizontal_flip: 'Horizontaal spiegelen',
    how_to: {
      text: 'Hoe maak je een stempel?',
      url: 'https://support.flux3dp.com/hc/en-us/articles/13658787903247',
    },
    invert: 'Omkeren',
    title: 'Stempelmaker',
  },
  support: {
    no_vcredist: 'Installeer Visual C++ Redistributable 2015<br/>Downloadbaar op flux3dp.com',
    no_webgl: 'WebGL wordt niet ondersteund. Gebruik een ander apparaat.',
  },
  tab_panel: {
    apply: 'Tabs toepassen',
    gap: 'Tussenruimte',
    help_center: 'https://support.flux3dp.com/hc/en-us/articles/12441363882511',
    manual_mode: 'Handmatige modus',
    title: 'Tabs',
  },
  tips: {
    canvas_hold_space_to_pan: 'Houd de spatiebalk ingedrukt en sleep om over het canvas te bewegen.',
    feature_auto_fit: 'Na cameravoorbeeld kunt u Acties → Automatisch aanpassen gebruiken om objecten automatisch op uw materiaal te plaatsen.',
    feature_box_generator: 'Gebruik Generatoren → Box Generator om in enkele seconden dozen van aangepast formaat te maken.',
    feature_code_generator: 'Gebruik Generatoren → Barcode Generator om QR-codes en barcodes te maken.',
    feature_elements: 'Het Elements-paneel bevat nu meer dan een miljoen graphics die klaar zijn voor gebruik.',
    feature_layer_color_configurations: 'Koppel SVG-laagkleuren aan snelheid en vermogen via Bewerken → Laag → Kleurinstellingen.',
    feature_material_test: 'Gebruik Generatoren → Material Test Generator in de linkerwerkbalk om snel de beste instellingen voor een materiaal te vinden.',
    layer_execution_order: 'De laagvolgorde bepaalt de taakvolgorde. Taken worden van boven naar beneden uitgevoerd.',
    machine_clean_mirror: 'Controleer de spiegels en focuslens regelmatig en reinig ze indien nodig om ophoping van rook te voorkomen.',
    machine_no_unattended_operation: 'Laat de machine nooit onbeheerd draaien.',
    machine_regular_start: 'Voer minstens eens per twee weken een lasertaak uit om de levensduur van de laserbuis te verlengen.',
    object_edit_image_to_erase: 'Gebruik Afbeelding → Acties → Afbeelding bewerken om ongewenste gebieden in een afbeelding te wissen.',
    object_polygon_sides: 'Pas het aantal zijden aan in het eigenschappenpaneel van de veelhoek.',
    object_rounded_rectangle: 'Stel de hoekradius van een afgeronde rechthoek in in het eigenschappenpaneel van de rechthoek.',
    object_sharpen_portrait: 'Verbeter portretgravures met Afbeelding → Acties → Verscherpen.',
    settings_auto_save: 'Schakel Automatisch opslaan in Voorkeuren in om te voorkomen dat u uw werk verliest.',
    settings_default_text_font: 'U kunt uw standaardlettertype voor de teksttool instellen in Voorkeuren.',
    task_focus_lower_for_cutting: 'Voor dikkere materialen kan focussen ongeveer 2 mm onder het oppervlak de snijresultaten verbeteren.',
    task_leather_moisten_reduce_burn: 'Bevochtig leer licht voordat u het snijdt om brandvlekken te verminderen.',
    task_parameters_for_promark_color: 'Pas pulsbreedte en frequentie aan op MOPA-lasers om kleuren te produceren op roestvrij staal of titanium.',
    task_slower_for_smoother_acrylic_edge: 'Het iets verlagen van de snijsnelheid kan gladdere acrylranden produceren.',
    tips: 'Tips:',
  },
  topbar: {
    alerts: {
      add_content_first: 'Voeg eerst objecten toe',
      close_door_before_framing: 'Sluit de deur voordat u de kadering-voorbeeldweergave start.',
      door_opened: 'Sluit het deksel om laag laservermogen voor het uitvoeren van frames in te schakelen.',
      fail_to_connect_with_camera: '#803 Kan geen verbinding maken met machinecamera. Start uw machine of Beam Studio opnieuw op. Als deze fout zich blijft voordoen, volg dan <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/360001791895">deze handleiding</a>.',
      fail_to_get_door_status: 'Zorg ervoor dat het deksel gesloten is om laag laservermogen voor het uitvoeren van frames in te schakelen.',
      fail_to_start_preview: '#803 Kan voorbeeldmodus niet starten. Start uw machine of Beam Studio opnieuw op. Als deze fout zich blijft voordoen, volg dan <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/360001791895">deze handleiding</a>.',
      headtype_mismatch: 'Onjuiste module gedetecteerd. ',
      headtype_none: 'Module niet gedetecteerd. ',
      headtype_unknown: 'Onbekende module gedetecteerd. ',
      install_correct_headtype: 'Installeer de 10W/20W diode lasermodules correct om laag laservermogen voor het uitvoeren van frames in te schakelen.',
      job_origin_unavailable: 'Het instellen van de taakbron vereist firmwareversie 4.3.5 / 5.3.5 of hoger. Wilt u de firmware nu bijwerken?',
      job_origin_warning: 'U gebruikt momenteel de “huidige positie” als startpunt. Zorg ervoor dat de laserkop naar de juiste positie wordt verplaatst om botsingen te voorkomen.',
      object_outside_moving_area: 'Sommige objecten bevinden zich buiten het voorbeeldkader.',
      power_too_high: 'TE HOGE SPANNING',
      power_too_high_confirm: 'BEGREPEN',
      power_too_high_msg: 'Gebruik een lager laservermogen (onder 70%) om de levensduur van de lasertube te verlengen.\nVoer "BEGREPEN" in om door te gaan.',
      pwm_unavailable: 'Dieptemodus vereist firmwareversie 4.3.4 / 5.3.4 of hoger. Wilt u de firmware nu bijwerken?',
      QcleanScene: 'Wil je de tekening wissen?<br/>Dit zal ook je undo-geschiedenis wissen!',
      start_preview_timeout: '#803 Time-out opgetreden bij het starten van de voorbeeldmodus. Start uw machine of Beam Studio opnieuw op. Als deze fout zich blijft voordoen, volg dan <a target="_blank" href="https://support.flux3dp.com/hc/en-us/articles/360001791895">deze handleiding</a>.',
      variable_text_warning: 'Deze taak bevat variabele tekst. Het systeem zal eerst de variabele tekst graveren en volgt niet de laagvolgorde.',
    },
    frame_task: 'Kadrering',
    hint: {
      polygon: 'Druk op + / - om het aantal zijden te verhogen / verlagen.',
    },
    menu: {
      about: 'Over Beam Studio',
      about_beam_studio: 'Over Beam Studio',
      accessories: 'Accessoires',
      account: 'Account',
      add_new_machine: 'Machine-instellingen',
      align_center: 'Centreren',
      anti_aliasing: 'Antialiasing',
      auto_align: 'Automatisch Uitlijnen',
      autofocus: {
        operating: 'Automatisch scherpstellen...',
        select_device_error: 'Selecteer een apparaat vóór automatisch scherpstellen.',
        show_probe_error: 'Toon de sonde vóór automatisch scherpstellen.',
        title: 'Autofocus',
      },
      borderless_mode: 'Randloze modus',
      bug_report: 'Fout melden',
      calibrate_beambox_camera: 'Camera kalibreren',
      calibrate_beambox_camera_borderless: 'Camera kalibreren (open onderkant)',
      calibrate_camera_advanced: 'Camera kalibreren (Geavanceerd)',
      calibrate_diode_module: 'Diodelasermodule kalibreren',
      calibrate_ir_module: 'Infraroodmodule kalibreren',
      calibrate_printer_module: 'Printmodule kalibreren',
      calibrate_wide_angle_camera: 'Camera kalibreren (Groothoekcamera)',
      calibration: 'Kalibraties',
      camera_calibration_data: 'Camera kalibratiegegevens',
      change_logs: 'Wijzigingslogboeken',
      clear_scene: 'Nieuwe bestanden',
      close: 'Venster sluiten',
      commands: "Commando's",
      contact: 'Contact',
      copy: 'Kopiëren',
      cut: 'Knippen',
      dashboard: 'Dashboard',
      decompose_path: 'Ontleden',
      delete: 'Verwijderen',
      design_market: 'Ontwerpmarkt',
      dev_tool: 'Debug-tool',
      disassemble_use: 'Demonteren',
      document_setting: 'Documentinstellingen',
      document_setting_short: 'Document',
      download_log: 'Logs downloaden',
      download_log_canceled: 'Log download geannuleerd',
      download_log_error: 'Onbekende fout opgetreden, later opnieuw proberen',
      duplicate: 'Dupliceren',
      edit: 'Bewerken',
      example_files: 'Voorbeeldbestanden',
      export_BVG: 'BVG',
      export_calibration_data: 'Kalibratiegegevens exporteren',
      export_flux_task: 'FLUX taak',
      export_JPG: 'JPG',
      export_PNG: 'PNG',
      export_SVG: 'SVG',
      export_to: 'Exporteren naar...',
      export_UV_print: 'UV-print PDF',
      file: 'Bestand',
      fit_to_window: 'Aanpassen aan venster',
      follow_us: 'Volg ons',
      forum: 'Communityforum',
      group: 'Groeperen',
      help: 'Help',
      help_center: 'Hulppagina',
      hide: 'Verberg Beam Studio',
      hideothers: 'Anderen verbergen',
      image_crop: 'Bijsnijden',
      image_curve: 'Kromme',
      image_invert: 'Inverteren',
      image_sharpen: 'Scherpstellen',
      image_stamp: 'Facet',
      image_vectorize: 'Vectoriseren',
      import_acrylic_focus_probe: 'beamo & Beambox Focusprobe - 3 mm',
      import_ador_laser_example: 'Voorbeeld van Ador Laser',
      import_ador_printing_example_full: 'Voorbeeld van Ador Printing - Volledige kleur',
      import_ador_printing_example_single: 'Voorbeeld van Ador Printing - Enkele kleur',
      import_beambox_2_example: 'Beambox II voorbeeld ',
      import_beambox_2_focus_probe: 'Beambox II Focusprobe - 3 mm',
      import_beamo_2_laser_example: 'Voorbeeld van beamo II Laser',
      import_beamo_2_printing_example: 'Voorbeeld van beamo II Printing',
      import_beamo_2_printing_test: 'beamo II Printtest',
      import_calibration_data: 'Kalibratiegegevens importeren',
      import_hello_beambox: 'Beambox voorbeeld',
      import_hello_beamo: 'beamo voorbeeld',
      import_hexa_example: 'Voorbeeld van HEXA',
      import_hexa_rf_example: 'Voorbeeld van HEXA RF',
      import_material_printing_test: 'Materiaal printtest',
      import_material_testing_cut: 'Materiaal snijtest',
      import_material_testing_engrave: 'Materiaal graveertest',
      import_material_testing_line: 'Materiaal testlijn',
      import_material_testing_old: 'Materiaal graveertest - Klassiek',
      import_material_testing_simple_cut: 'Materiaal snijtest - Eenvoudig',
      import_promark_engraving_example_1: 'Voorbeeld van Promark-gravure I',
      import_promark_engraving_example_2: 'Voorbeeld van Promark-gravure II',
      import_promark_example: 'Voorbeeld van Promark',
      import_promark_mopa_20w_color: 'MOPA 20W Kleurtest',
      import_promark_mopa_60w_color: 'MOPA 60W Kleurtest',
      import_promark_mopa_100w_color: 'MOPA 100W Kleurtest',
      keyboard_shortcuts: 'Toetsencombinaties',
      layer_color_config: 'Kleurinstellingen',
      layer_setting: 'Laag',
      link: {
        autocad: 'https://support.flux3dp.com/hc/en-us/articles/16590795274895',
        beam_studio_api: 'https://github.com/flux3dp/beam-studio/wiki/Beam-Studio-Easy-API',
        contact_us: 'https://support.flux3dp.com/hc/en-us/requests/new',
        design_market: 'https://dmkt.io',
        downloads: 'https://flux3dp.com/downloads/',
        forum: 'https://www.facebook.com/groups/flux.laser/',
        help_center: 'https://helpcenter.flux3dp.com/',
        shortcuts: 'https://support.flux3dp.com/hc/en-us/articles/10003978157455',
      },
      log: {
        camera: 'Camera',
        cloud: 'Cloud',
        discover: 'Ontdekken',
        hardware: 'Hardware',
        network: 'Netwerk',
        player: 'Speler',
        robot: 'Robot',
        usb: 'USB',
        usblist: 'USB-lijst',
      },
      login_or_register: 'Inloggen of registreren',
      logout: 'Uitloggen',
      machine_info: 'Machinegegevens',
      machines: 'Machines',
      maintenance_checklist: 'Onderhoudschecklist',
      manage_account: 'Mijn account beheren',
      material_test: 'Materiaaltest',
      minimize: 'Minimaliseren',
      my_account: 'Mijn account',
      network_testing: 'Netwerkinstellingen testen',
      object: 'Object',
      offset: 'Verschuiven',
      open: 'Openen',
      paste: 'Plakken',
      paste_in_place: 'Plakken op plaats',
      path: 'Pad',
      photo_edit: 'Afbeelding',
      plugin: 'Plug-in',
      preferences: 'Voorkeuren',
      questionnaire: 'Vragenlijst feedback',
      quit: 'Afsluiten',
      recent: 'Open recente bestanden',
      redo: 'Opnieuw',
      reload_app: 'App opnieuw laden',
      reset: 'Resetten',
      reset_layout: 'Indeling herstellen',
      rotary_setup: 'Draaibare Instelling',
      rotate: 'Draaien',
      samples: 'Voorbeelden',
      save_as: 'Opslaan als...',
      save_scene: 'Opslaan',
      save_to_cloud: 'Opslaan in cloud',
      scale: 'Schalen',
      service: 'Diensten',
      set_as_default: 'Instellen als standaard',
      show_gesture_tutorial: 'Introductie handgebaren',
      show_grids: 'Rasters weergeven',
      show_layer_color: 'Laagkleur gebruiken',
      show_rulers: 'Linialen tonen',
      show_start_tutorial: 'Introductietutorial tonen',
      show_ui_intro: 'Interface-introductie tonen',
      software_update: 'Software-update',
      svg_edit: 'SVG',
      switch_to_beta: 'Schakel over naar bètaversie',
      switch_to_latest: 'Schakel over naar stabiele release',
      tab_layers: 'Lagen',
      tab_objects: 'Objecten',
      tab_path_edit: 'Pad bewerken',
      tools: {
        title: 'Gereedschappen',
      },
      tutorial: 'Delta-familie printtutorial starten',
      undo: 'Ongedaan maken',
      ungroup: 'Degroeperen',
      update: 'Vink Bijwerken aan',
      update_firmware: 'Firmware bijwerken',
      update_machine: 'Machine bijwerken',
      update_mainboard: 'Hoofdboard bijwerken',
      update_printer_board: 'Printerboard bijwerken',
      using_beam_studio_api: 'Beam Studio API gebruiken',
      view: 'Weergave',
      window: 'Venster',
      zoom_in: 'Inzoomen',
      zoom_out: 'Uitzoomen',
      zoom_with_window: 'Automatisch aanpassen aan venster',
    },
    rename_tab: 'Tab hernoemen',
    select_machine: 'Selecteer een machine',
    tag_names: {
      dxf: 'DXF-object',
      ellipse: 'Ovaal',
      fit_text: 'Tekstvak',
      g: 'Groep',
      image: 'Afbeelding',
      line: 'Lijn',
      multi_select: 'Meerdere objecten',
      no_selection: 'Geen selectie',
      pass_through_object: 'Object Doorvoeren',
      path: 'Pad',
      polygon: 'Veelhoek',
      rect: 'Rechthoek',
      svg: 'SVG-object',
      text: 'Tekst',
      text_path: 'Tekst op pad',
      use: 'Geïmporteerd object',
    },
    task_preview: 'Padvoorbeeld',
    untitled: 'Naamloos',
  },
  topmenu: {
    credit: 'Beam Studio is mogelijk gemaakt door het <a target="_blank" href="https://github.com/flux3dp/beam-studio">Beam Studio</a> open source project en andere <a target="_blank" href="https://flux3dp.com/credits/">open source software</a>.',
    device: {
      download_log_canceled: 'Logboekdownload geannuleerd',
      download_log_error: 'Onbekende fout opgetreden, probeer het later opnieuw',
      log: {
        usblist: 'USB-lijst',
      },
      network_test: 'Netwerk testen',
    },
    file: {
      all_files: 'Alle bestanden',
      clear_recent: 'Recent geopende wissen',
      converting: 'Converteren naar afbeelding...',
      fcode_files: 'FLUX-code',
      import: 'Importeren',
      jpg_files: 'JPG',
      label: 'Bestand',
      path_not_exit: 'Dit pad lijkt niet meer te bestaan op de schijf.',
      png_files: 'PNG',
      save_fcode: 'Exporteer FLUX-taak',
      save_jpg: 'Exporteer JPG',
      save_pdf: 'PDF exporteren',
      save_png: 'Exporteer PNG',
      save_scene: 'Scene opslaan',
      save_svg: 'Exporteer SVG',
      scene_files: 'Beam Studio-scène',
      svg_files: 'SVG',
    },
    version: 'Versie',
  },
  tutorial: {
    ask_retry_calibration: 'Wilt u de camera opnieuw kalibreren?',
    camera_calibration_failed: 'Camerakalibratie mislukt',
    gesture: {
      click: 'Tik om het object te selecteren.',
      drag: 'Sleep om meerdere objecten te selecteren.',
      hold: 'Houd ingedrukt om het contextmenu te openen.',
      pan: 'Scroll het canvas met twee vingers.',
      zoom: 'Knijp met twee vingers in/uit om in/uit te zoomen op het canvas.',
    },
    links: {
      adjust_focus: 'https://support.flux3dp.com/hc/en-us/articles/360001684196',
    },
    look_for_machine: 'Machine zoeken voor tutorial...',
    needNewInterfaceTutorial: 'Wilt u een tutorial starten voor de nieuwe interface van Beam Studio?<br/>(U kunt het nu overslaan en later starten door te klikken op "Help" > "Toon Interface Introductie".)',
    needNewUserTutorial: 'Wilt u een tutorial starten?<br/>(U kunt het nu overslaan en later starten door te klikken op "Help" > "Toon Starttutorial".)',
    newInterface: {
      add_new_layer: 'Voeg nieuwe laag toe',
      align_controls: 'Lijn uitlijnen',
      basic_shapes: 'Basisvormen',
      camera_preview: 'Camera voorbeeld',
      drag_to_sort: 'Sleep om te sorteren',
      end_alert: 'Weet u zeker dat u de nieuwe UI-introductie wilt afsluiten?',
      flip: 'Spiegelen',
      group_controls: 'Groepeer',
      layer_controls: 'Rechtsklik voor laagopties:\nDupliceren / Samenvoegen / Vergrendelen / Lagen verwijderen',
      object_actions: 'Objectacties',
      pen_tool: 'Pen tool',
      rename_by_double_click: 'Hernoem door dubbel te klikken',
      select_image_text: 'Selecteer afbeelding / tekst',
      select_machine: 'Selecteer een machine',
      shape_operation: 'Vormbewerking',
      start_work: 'Begin met werken',
      switch_between_layer_panel_and_object_panel: 'Schakel tussen laagpaneel en objectpaneel',
    },
    newUser: {
      add_new_layer: 'Voeg een nieuwe laag toe',
      adjust_focus: '2. Stel de focus in',
      close_cover: '3. Sluit het deksel',
      drag_to_draw: 'Sleep om te tekenen',
      draw_a_circle: 'Teken een cirkel',
      draw_a_rect: 'Teken een rechthoek',
      end_alert: 'Weet u zeker dat u de zelfstudie wilt afsluiten?',
      end_preview_mode: 'Voorbeeldmodus beëindigen',
      infill: 'Infill inschakelen',
      please_select_wood_cutting: 'Selecteer a.u.b. de voorinstelling "Hout - Snijden".',
      please_select_wood_engraving: 'Selecteer a.u.b. de voorinstelling "Hout - Graveren".',
      preview_the_platform: 'Bekijk het platform',
      put_wood: '1. Plaats het houtmonster',
      send_the_file: 'Verstuur het bestand',
      set_preset_wood_cut: 'Stel voorinstelling in: Hout - Snijden',
      set_preset_wood_engraving: 'Stel voorinstelling in: Hout - Graveren',
      start_tutorial: 'Introductietutorial',
      switch_to_layer_panel: 'Schakel over naar laagvenster',
      switch_to_object_panel: 'Schakel over naar Objectenpaneel',
      switch_to_preview_mode: 'Schakel over naar voorbeeldmodus',
    },
    next: 'VOLGENDE',
    retry: 'Opnieuw proberen',
    set_connection: 'Verbinding instellen',
    skip: 'Overslaan',
    skip_tutorial: 'U heeft de tutorial overgeslagen. U kunt altijd de tutorial starten door te klikken op "Help" > "Tutorial tonen"',
    suggest_calibrate_camera_first: 'We adviseren gebruikers om de camera aanvankelijk te kalibreren en opnieuw scherp te stellen voor elke voorbeeldweergave voor optimale resultaten.<br/>Wilt u bevestigen om nu de kalibratie uit te voeren?<br/>(U kunt het nu overslaan en later uitvoeren door te klikken op "Menu" > "Machine" > [Uw Machine] > "Camera Kalibreren".)',
    tutorial_complete: 'Dat is alles voor de zelfstudie. Nu is het tijd om te creëren!',
    unable_to_find_machine: 'Kan geen machine vinden voor tutorial. Wilt u naar de verbindingsinstellingenpagina gaan, de tutorial opnieuw proberen of overslaan?',
    welcome: 'WELKOM',
  },
  update: {
    cannot_reach_internet: '#823 Server is niet bereikbaar<br/>Controleer de internetverbinding',
    download: 'ONLINE UPDATE',
    firmware: {
      caption: 'Er is een firmware-update voor de machine beschikbaar',
      confirm: 'UPLOADEN',
      firmware_too_old_update_by_sdcard: 'De firmwareversie is te oud. Werk de firmware bij met behulp van een SD-kaart.',
      force_update_message: '#814 Werk uw machine bij naar de nieuwste firmwareversie.',
      latest_firmware: {
        cant_get_latest: 'Kan geen informatie krijgen over de nieuwste firmware.',
        caption: 'Werk machinefirmware bij',
        message: 'U heeft de nieuwste machinefirmware',
        still_update: 'BIJWERKEN',
      },
      message_pattern_1: '"%s" is nu klaar voor een firmware-update.',
      message_pattern_2: '%s firmware v%s is nu beschikbaar - U heeft v%s.',
      too_old_for_web: 'Uw huidige machinefirmwareversie is v%s.\nAls u de online versie van Beam Studio wilt gebruiken, werk dan de machinefirmware bij naar de nieuwste versie.',
      update_fail: '#822 Update mislukt',
      update_success: 'Firmware-update succesvol geüpload',
      upload_firmware_title: 'Firmware uploaden (*.fxfw)',
      upload_mainboard_title: 'Firmware uploaden naar moederbord (*.bin)',
      upload_printer_board_title: 'Firmware uploaden naar printerbord (*.bin)',
    },
    install: 'INSTALLEREN',
    later: 'LATER',
    preparing: 'Voorbereiden...',
    release_note: 'Release opmerkingen:',
    skip: 'Deze versie overslaan',
    software: {
      available_switch: 'Beam Studio v%s is nu beschikbaar. U heeft versie %s. Wilt u upgraden naar deze versie?',
      available_update: 'Beam Studio v%s is nu beschikbaar. U heeft versie %s. Wilt u de update downloaden?',
      caption: 'Er is een software-update voor Beam Studio beschikbaar',
      check_update: 'Controleren op updates',
      checking: 'Controleren op updates',
      downloading: 'Updates worden op de achtergrond gedownload, u kunt op "OK" klikken om door te gaan met uw werk.',
      install_or_not: 'is klaar voor update. Wilt u nu opnieuw opstarten en updaten?',
      no: 'Nee',
      no_response: 'Kan geen verbinding maken met server, controleer uw netwerkinstellingen.',
      not_found: 'U gebruikt de nieuwste versie van Beam Studio.',
      switch_or_not: 'is klaar om te wisselen. Wilt u nu opnieuw opstarten en wisselen?',
      switch_version: 'Versie wisselen',
      switch_version_not_found: 'Te upgraden versie niet gevonden.',
      update_for_ador: 'De huidige softwareversie %s is niet compatibel, download de nieuwste versie van Beam Studio voor Ador.',
      yes: 'Ja',
    },
    update: 'Bijwerken',
    updating: 'Bezig met updaten...',
    upload: 'UPLOADEN',
  },
  variable_text_settings: {
    advance_by: 'Vooruit met',
    auto_advance: 'Automatisch doorgaan',
    auto_advance_hint: 'Gaat automatisch verder naar de volgende variabele gegevens bij elke taakuitvoering.',
    bake: 'Afronden',
    bake_description: 'Variabiliteit wissen en omzetten naar normale tekst',
    browse: 'Bladeren',
    clear: 'Wissen',
    csv_file: 'CSV-bestand',
    current: 'Huidig',
    end: 'Einde',
    failed_to_convert: 'Het object met variabele tekst bevat een niet-ondersteund formaat, waardoor de inhoud mogelijk niet wordt weergegeven.',
    file_error: 'CSV-bestand kon niet worden verwerkt, controleer het bestandsformaat.',
    file_size_exceeded: 'Bestand upload mislukt, bestandsgrootte overschrijdt 5 MB.',
    next: 'Volgende',
    note: 'Deze instelling wordt automatisch toegepast op alle variabele teksten',
    previous: 'Vorige',
    start: 'Start',
    test: 'Test',
    test_description: 'Houd ingedrukt om variabele tekst te bekijken',
    title: 'Instellingen voor variabele teksten',
  },
  web_cam: {
    image_to_small: 'De camerabeeldgrootte is te klein. De afbeelding kan beschadigd zijn.',
    no_device: 'Kan camera-apparaat niet detecteren. Sluit de camera opnieuw aan en probeer het opnieuw.',
    no_permission: 'Beam Studio heeft geen toestemming om toegang te krijgen tot de camera. Zorg ervoor dat de toestemming is verleend aan Beam Studio in de browser- of systeeminstellingen.',
  },
  welcome_page: {
    follow_us: {
      subtitle: 'Ontvang inspiratie, aanbiedingen, gratis items en kom in contact met FLUX-gebruikers!',
      user_group: 'Gebruikersgroep',
    },
    help_center: {
      guides: 'Gidsen',
      guides_subtitle: 'Bekijk uitgebreide gidsen voor alle FLUX-producten.',
      submit_request: 'Een verzoek indienen',
      subtitle: 'Handleidingen, manuals en ondersteuningsbronnen voor alles van FLUX.',
      visit_help_center: 'Bezoek het Helpcentrum',
    },
    member_center: 'Ledencentrum',
    member_center_url: 'https://member.flux3dp.com/en-US/machine-register',
    new_project: 'Nieuw project',
    not_login_placeholder: 'Ontvang gratis AI-tegoed van ons wanneer u zich aanmeldt voor een FLUX-account!',
    recent_files: 'Recente bestanden',
    shop: 'Winkel',
    shop_products: 'FLUX-producten kopen',
  },
  z_speed_limit_test: {
    alert_before: 'Voordat de krommegravure begint, voert het systeem een belastingstest uit om verlies van stappen door overmatig gewicht te voorkomen.',
    alert_failed: 'Het gewicht van het huidige object overschrijdt de belastinglimiet voor de huidige Z-as snelheid. Verlaag de Z-as snelheid op het machinepaneel of vervang het graveerobject voordat u opnieuw test.',
    ignore: 'Negeren',
    retest: 'Opnieuw testen',
    testing: 'Bezig met het uitvoeren van de belastingtest...',
  },
};

export default lang;
